
import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'motion/react';
import { AgendaEntry, LearningObjective, ScheduleEntry, ActivityType, View, Teacher, Student, AttendanceRecord, AttendanceStatus } from '../types.ts';
import { getThemeById } from '../src/themeConfig.ts';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { 
  PlusCircle, 
  Edit, 
  Trash2, 
  Calendar, 
  CalendarDays,
  X, 
  CheckCircle2, 
  BookMarked, 
  Target, 
  Clock, 
  Info, 
  ChevronRight, 
  Tags,
  LayoutList,
  History,
  Search,
  ArrowUpDown,
  Filter,
  UserCheck,
  FileSpreadsheet,
  Link as LinkIcon,
  ExternalLink,
  ArrowRight,
  MessageSquare,
  Copy,
  Printer,
  Eye,
  Upload,
  Loader2,
  AlertCircle,
  Archive
} from 'lucide-react';
import { supabase } from '../services/supabase';

const safeFormatDate = (dateStr: string | null | undefined, options?: Intl.DateTimeFormatOptions, fallback: string = '-') => {
  if (!dateStr) return fallback;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return fallback;
  return d.toLocaleDateString('id-ID', options);
};

interface Props {
  agendas: AgendaEntry[];
  setAgendas: (agendas: AgendaEntry[]) => void;
  schedules: ScheduleEntry[];
  availableClasses: string[];
  teacherSubject: string;
  objectives: LearningObjective[];
  activeUser: Teacher;
  onNavigate: (view: View, params?: any) => void;
  students: Student[];
  attendanceRecords: AttendanceRecord[];
  onUpdateAttendance: (records: AttendanceRecord[]) => void;
  schoolSettings: any;
  initialAgenda?: AgendaEntry | null;
  initialTab?: 'input' | 'recap';
  appTheme?: string;
}

const DirectAttendanceModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  agenda: AgendaEntry;
  students: Student[];
  records: AttendanceRecord[];
  onSave: (newRecords: AttendanceRecord[]) => void;
}> = ({ isOpen, onClose, agenda, students, records, onSave }) => {
  const classStudents = students.filter(s => s.class === agenda.class && s.status === 'Aktif').sort((a,b) => a.name.localeCompare(b.name));
  const [localAttendance, setLocalAttendance] = useState<Record<string, { status: AttendanceStatus, description?: string }>>({});
  const [dispenModal, setDispenModal] = useState<{ studentId: string, name: string } | null>(null);

  useEffect(() => {
    const agendaRecords = records.filter(r => r.agendaId === agenda.id);
    const initial: Record<string, { status: AttendanceStatus, description?: string }> = {};
    classStudents.forEach(s => {
      const rec = agendaRecords.find(r => r.studentId === s.id);
      initial[s.id] = rec ? { status: rec.status, description: rec.description } : { status: AttendanceStatus.Hadir };
    });
    setLocalAttendance(initial);
  }, [agenda, records, students, isOpen]);

  if (!isOpen) return null;

  const handleStatusChange = (studentId: string, status: AttendanceStatus) => {
    if (status === AttendanceStatus.Dispen) {
        const student = students.find(s => s.id === studentId);
        setDispenModal({ studentId, name: student?.name || 'Siswa' });
    } else {
        setLocalAttendance(prev => ({ ...prev, [studentId]: { status } }));
    }
  };

  const handleSaveAll = () => {
    const otherRecords = records.filter(r => r.agendaId !== agenda.id);
    const newAgendaRecords: AttendanceRecord[] = classStudents.map(s => ({
      id: `att-${agenda.id}-${s.id}`,
      studentId: s.id,
      agendaId: agenda.id,
      date: agenda.date,
      status: localAttendance[s.id]?.status || AttendanceStatus.Hadir,
      description: localAttendance[s.id]?.description,
      class: s.class,
      teacherId: agenda.teacherId
    }));
    onSave([...otherRecords, ...newAgendaRecords]);
    onClose();
  };

  const getStudentNameColor = (gender?: string) => {
    const g = (gender || '').trim().toLowerCase();
    const isFemale = g === 'perempuan' || g === 'p' || g === 'female' || g === 'f';
    return isFemale ? 'text-pink-600 dark:text-pink-400' : 'text-slate-800 dark:text-white';
  };

  const statusConfig: Record<string, { short: string; activeBg: string }> = {
    [AttendanceStatus.Hadir]: { short: 'H', activeBg: 'bg-emerald-600 dark:bg-emerald-600' },
    [AttendanceStatus.Izin]: { short: 'I', activeBg: 'bg-blue-600 dark:bg-blue-600' },
    [AttendanceStatus.Sakit]: { short: 'S', activeBg: 'bg-amber-600 dark:bg-amber-600' },
    [AttendanceStatus.Alpa]: { short: 'A', activeBg: 'bg-rose-600 dark:bg-rose-600' },
    [AttendanceStatus.Dispen]: { short: 'D', activeBg: 'bg-purple-600 dark:bg-purple-600' },
    [AttendanceStatus.Terlambat]: { short: 'T', activeBg: 'bg-orange-600 dark:bg-orange-600' },
    [AttendanceStatus.Pulang]: { short: 'P', activeBg: 'bg-teal-600 dark:bg-teal-600' },
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-[2px] z-[150] flex justify-center items-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl w-full max-w-2xl p-8 relative animate-fade-in-up border border-slate-100 dark:border-slate-800">
        <button onClick={onClose} className="absolute top-6 right-6 text-slate-400 hover:text-slate-600 transition-colors"><X size={24} /></button>
        <div className="mb-6">
            <h3 className="text-2xl font-black text-slate-800 dark:text-white leading-tight">Absensi Kelas {agenda.class}</h3>
            <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest mt-1">
                Pertemuan: {agenda.topic} • {safeFormatDate(agenda.date, { day: '2-digit', month: 'long' })}
            </p>
        </div>

        <div className="max-h-[50vh] overflow-y-auto px-1 custom-scrollbar mb-8">
            <table className="w-full text-sm border-separate border-spacing-y-2">
                <tbody>
                    {classStudents.map((student, idx) => {
                        const current = localAttendance[student.id] || { status: AttendanceStatus.Hadir };
                        return (
                            <tr key={student.id}>
                                <td className="py-3 px-4 bg-slate-50/50 dark:bg-slate-800/50 rounded-l-2xl border-y border-l border-r border-slate-200 dark:border-slate-700">
                                    <div className="flex items-center gap-2.5">
                                        <span className="w-6 h-6 rounded-lg bg-slate-200/80 dark:bg-slate-700 text-slate-700 dark:text-slate-300 font-black text-[11px] flex items-center justify-center shrink-0">
                                            {idx + 1}
                                        </span>
                                        <div className="min-w-0">
                                            <p className={`font-black text-xs ${getStudentNameColor(student.gender)}`}>
                                                {idx + 1}. {student.name}
                                            </p>
                                            {current.description && <p className="text-[9px] text-purple-500 font-bold truncate max-w-[150px]">{current.description}</p>}
                                        </div>
                                    </div>
                                </td>
                                <td className="py-3 px-4 bg-slate-50/50 dark:bg-slate-800/50 rounded-r-2xl text-right border-y border-r border-slate-200 dark:border-slate-700">
                                    <div className="flex justify-end gap-1.5">
                                        {Object.values(AttendanceStatus).map(st => (
                                            <button 
                                                key={st}
                                                onClick={() => handleStatusChange(student.id, st)}
                                                className={`w-8 h-8 rounded-xl font-black text-[10px] transition-all border shadow-sm ${current.status === st ? `${statusConfig[st].activeBg} text-white border-transparent` : 'bg-white dark:bg-slate-900 text-slate-400 border-slate-200 dark:border-slate-700'}`}
                                            >
                                                {statusConfig[st].short}
                                            </button>
                                        ))}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>

        <div className="flex justify-end gap-3">
            <button onClick={onClose} className="px-6 py-3 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 font-black rounded-2xl text-xs uppercase tracking-widest">Batal</button>
            <button onClick={handleSaveAll} className="px-10 py-3 bg-indigo-600 text-white font-black rounded-2xl shadow-xl shadow-indigo-500/20 text-xs uppercase tracking-widest">Simpan Absensi</button>
        </div>

        {/* INNER MODAL DISPEN */}
        {dispenModal && (
            <div className="fixed inset-0 z-[160] flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" onClick={() => setDispenModal(null)}></div>
                <div className="bg-white dark:bg-slate-900 w-full max-w-sm rounded-[2rem] p-8 relative shadow-2xl">
                    <h4 className="text-lg font-black mb-4">Keterangan Dispen: {dispenModal.name}</h4>
                    <textarea 
                        id="direct-dispen-notes"
                        className="w-full p-4 bg-slate-50 dark:bg-slate-800 border rounded-2xl outline-none font-bold text-sm mb-4"
                        placeholder="Alasan dispen..."
                        rows={3}
                    />
                    <div className="flex justify-end gap-2">
                        <button onClick={() => setDispenModal(null)} className="px-4 py-2 font-black text-xs text-slate-400 uppercase tracking-widest">Batal</button>
                        <button 
                            onClick={() => {
                                const description = (document.getElementById('direct-dispen-notes') as HTMLTextAreaElement).value;
                                setLocalAttendance(prev => ({ ...prev, [dispenModal.studentId]: { status: AttendanceStatus.Dispen, description } }));
                                setDispenModal(null);
                            }}
                            className="px-6 py-2 bg-purple-600 text-white font-black rounded-xl text-xs uppercase tracking-widest"
                        >
                            Konfirmasi
                        </button>
                    </div>
                </div>
            </div>
        )}
      </div>
    </div>
  );
};

const compressImage = async (file: File, maxSizeKB: number = 600): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        const MAX_WIDTH = 1920;
        const MAX_HEIGHT = 1920;
        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width;
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height;
            height = MAX_HEIGHT;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(file);
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);

        let quality = 0.9;
        const compress = () => {
          canvas.toBlob((blob) => {
            if (!blob) {
              resolve(file);
              return;
            }
            if (blob.size / 1024 < maxSizeKB || quality <= 0.1) {
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              quality -= 0.1;
              compress();
            }
          }, 'image/jpeg', quality);
        };
        compress();
      };
      img.onerror = (error) => reject(error);
    };
    reader.onerror = (error) => reject(error);
  });
};

const AgendaModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSave: (entry: AgendaEntry) => void;
  entry: AgendaEntry | null;
  classes: string[];
  teacherSubject: string;
  objectives: LearningObjective[];
  suggestedStartPeriod?: number;
  suggestedEndPeriod?: number;
  suggestedClass?: string;
  suggestedDate?: string;
  isExtraAgendaParam?: boolean;
}> = ({ isOpen, onClose, onSave, entry, classes, teacherSubject, objectives, suggestedStartPeriod, suggestedEndPeriod, suggestedClass, suggestedDate, isExtraAgendaParam }) => {
  const isExtraMode = entry ? (!!entry.isExtra || entry.startPeriod === 0) : !!isExtraAgendaParam;
  
  const [formData, setFormData] = useState<AgendaEntry>({
    id: '', 
    date: suggestedDate || new Date().toISOString().split('T')[0], 
    class: suggestedStartPeriod === 0 ? '' : (suggestedClass || classes[0] || ''), 
    subject: teacherSubject, 
    topic: '',
    activityType: ActivityType.KBM, // Use enum
    penilaian: '',
    notes: '', 
    status: 'Belum Selesai', 
    tpId: '', 
    tpDescription: '',
    startPeriod: suggestedStartPeriod ?? 1,
    endPeriod: suggestedEndPeriod ?? suggestedStartPeriod ?? 1,
    isExtra: isExtraMode,
    isAttendanceOptional: false
  });
  
  // Also pass prop down when mounting
  // The rest is handled in the file block
  const [isUploading, setIsUploading] = useState(false);
  const [showDeleteImageConfirm, setShowDeleteImageConfirm] = useState(false);
  const [notification, setNotification] = useState<{message: string, type: 'success'|'error'} | null>(null);

  useEffect(() => {
    if (entry) {
      setFormData({
        ...entry,
        isExtra: entry.isExtra ?? (entry.startPeriod === 0), // fallback to true if startPeriod is 0
        isAttendanceOptional: entry.isAttendanceOptional ?? false
      });
    } else {
      setFormData({
        id: '', 
        date: suggestedDate || new Date().toISOString().split('T')[0], 
        class: suggestedStartPeriod === 0 ? '' : (suggestedClass || classes[0] || ''), 
        subject: teacherSubject, 
        topic: '',
        activityType: ActivityType.KBM, // Use enum
        penilaian: '',
        notes: '', 
        status: 'Belum Selesai', 
        tpId: '', 
        tpDescription: '',
        startPeriod: suggestedStartPeriod ?? 1,
        endPeriod: suggestedEndPeriod ?? suggestedStartPeriod ?? 1,
        isExtra: !!isExtraAgendaParam,
        isAttendanceOptional: false
      });
    }
  }, [entry, isOpen, suggestedStartPeriod, suggestedEndPeriod, suggestedClass, suggestedDate, classes, teacherSubject, isExtraAgendaParam]);

  if (!isOpen) return null;

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!supabase) {
      setNotification({ message: 'Konfigurasi Supabase belum diatur.', type: 'error' });
      setTimeout(() => setNotification(null), 3000);
      return;
    }

    setIsUploading(true);
    try {
      const compressedFile = await compressImage(file, 200);
      const fileExt = compressedFile.name.split('.').pop() || 'jpg';
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('PictureClass')
        .upload(filePath, compressedFile);

      if (uploadError) {
        throw uploadError;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('PictureClass')
        .getPublicUrl(filePath);

      setFormData(prev => ({ ...prev, documentationUrl: publicUrl }));
      setNotification({ message: 'Gambar berhasil diunggah!', type: 'success' });
      setTimeout(() => setNotification(null), 3000);
    } catch (error) {
      console.error('Error uploading image:', error);
      setNotification({ message: 'Gagal mengunggah gambar. Silakan coba lagi.', type: 'error' });
      setTimeout(() => setNotification(null), 3000);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteImage = async () => {
    if (!formData.documentationUrl) {
      setShowDeleteImageConfirm(false);
      return;
    }

    if (!supabase) {
      setFormData(prev => ({ ...prev, documentationUrl: '' }));
      setShowDeleteImageConfirm(false);
      return;
    }

    try {
      // Extract filename from the public URL
      const urlParts = formData.documentationUrl.split('/');
      let fileName = urlParts[urlParts.length - 1];
      fileName = fileName.split('?')[0]; // Remove query params if any
      fileName = decodeURIComponent(fileName); // Decode URL encoding

      if (fileName) {
        try {
          const response = await fetch(`/api/storage/PictureClass/${encodeURIComponent(fileName)}`, {
            method: 'DELETE',
          });
          if (!response.ok) {
            let errorData;
            try {
              const text = await response.text();
              errorData = text ? JSON.parse(text) : { error: 'Unknown error' };
            } catch (e) {
              errorData = { error: 'Failed to parse error response' };
            }
            console.error('Failed to delete image from bucket via API:', errorData);
            setNotification({ message: `Gagal menghapus gambar di bucket: ${errorData.error || 'Akses ditolak (Cek Policy RLS)'}`, type: 'error' });
            setTimeout(() => setNotification(null), 5000);
            setShowDeleteImageConfirm(false);
            return; // Stop here, don't clear UI
          }
        } catch (apiError) {
          console.error('Error calling delete API:', apiError);
          setNotification({ message: `Terjadi kesalahan jaringan saat menghapus gambar: ${apiError instanceof Error ? apiError.message : String(apiError)}`, type: 'error' });
          setTimeout(() => setNotification(null), 5000);
          setShowDeleteImageConfirm(false);
          return; // Stop here, don't clear UI
        }
      }

      setFormData(prev => ({ ...prev, documentationUrl: '' }));
      setNotification({ message: 'Gambar berhasil dihapus!', type: 'success' });
      setTimeout(() => setNotification(null), 3000);
    } catch (error) {
      console.error('Error in handleDeleteImage:', error);
      setNotification({ message: 'Terjadi kesalahan saat menghapus gambar.', type: 'error' });
      setTimeout(() => setNotification(null), 3000);
    } finally {
      setShowDeleteImageConfirm(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'tpId') {
      const selectedTp = objectives.find(o => o.id === value);
      setFormData(prev => ({ 
        ...prev, 
        tpId: value, 
        tpDescription: selectedTp ? selectedTp.description : '' 
      }));
    } else if (name === 'activityType') { // Handle enum conversion
        const newType = value as ActivityType;
        setFormData(prev => ({ 
          ...prev, 
          [name]: newType,
          // Automatically set attendance as optional for Tugas Mandiri and Sumatif Akhir
          isAttendanceOptional: (newType === ActivityType.TUGAS_MANDIRI || newType === ActivityType.SAS) ? true : prev.isAttendanceOptional 
        }));
    } else if (name === 'startPeriod' || name === 'endPeriod') {
        setFormData(prev => ({ ...prev, [name]: parseInt(value) || 1 }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleSave = () => {
    if (formData.status !== 'Jam kosong' && !formData.topic) {
        alert("Topik pembelajaran wajib diisi.");
        return;
    }
    onSave({ ...formData, id: formData.id || `agenda${Date.now()}` });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-md z-[100] flex justify-center items-center p-3 sm:p-5">
      <div className="bg-white/95 dark:bg-slate-900/95 rounded-[2.5rem] shadow-2xl w-full max-w-2xl p-6 sm:p-8 relative animate-fade-in-up border border-slate-200/80 dark:border-slate-800 max-h-[92vh] flex flex-col">
        
        {/* Notification Toast */}
        {notification && (
          <div className={`fixed top-4 left-1/2 -translate-x-1/2 px-6 py-3 rounded-2xl shadow-2xl flex items-center gap-3 animate-fade-in-down z-[9999] ${notification.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white'}`}>
            {notification.type === 'success' ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}
            <span className="text-sm font-bold">{notification.message}</span>
          </div>
        )}

        <button 
          onClick={onClose} 
          className="absolute top-6 right-6 p-2 rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-all cursor-pointer"
        >
          <X size={20} />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3.5 mb-6 shrink-0">
          <div className="p-3 bg-gradient-to-tr from-indigo-600 to-violet-500 text-white rounded-2xl shadow-lg shadow-indigo-500/25">
            <BookMarked size={22} />
          </div>
          <div>
            <h3 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight">
              {entry?.id ? 'Perbarui Agenda Harian' : 'Catat Agenda Baru'}
            </h3>
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
              {entry?.id ? 'Ubah rincian kegiatan mengajar' : 'Lengkapi informasi jurnal pengajaran'}
            </p>
          </div>
        </div>

        {/* Scrollable Form Content */}
        <div className="space-y-5 overflow-y-auto px-1 custom-scrollbar flex-1 pr-1">
          
          {/* Card 1: Informasi Jadwal & Kegiatan */}
          <div className="p-4 sm:p-5 bg-slate-50/80 dark:bg-slate-800/40 rounded-3xl border border-slate-200/60 dark:border-slate-700/50 space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-slate-200/60 dark:border-slate-700/50">
              <Calendar className="text-indigo-600 dark:text-indigo-400" size={16} />
              <span className="text-xs font-black text-slate-600 dark:text-slate-300 uppercase tracking-wider">Jadwal & Waktu Mengajar</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase block mb-1.5 ml-1">Tanggal Terpilih</label>
                <div className="w-full px-4 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-slate-700 dark:text-slate-200 font-bold text-sm flex items-center justify-between">
                  <span>{safeFormatDate(formData.date, { weekday: 'short', day: '2-digit', month: 'long', year: 'numeric' })}</span>
                  <CalendarDays size={16} className="text-indigo-500 shrink-0" />
                </div>
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase block mb-1.5 ml-1">Urutan Jam / Rombel</label>
                {isExtraMode ? (
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-2 items-center">
                      <label className="flex items-center gap-2 cursor-pointer text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase">
                        <input 
                          type="checkbox" 
                          checked={formData.startPeriod !== 0} 
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData(prev => ({ ...prev, startPeriod: 1, endPeriod: 1 }));
                            } else {
                              setFormData(prev => ({ ...prev, startPeriod: 0, endPeriod: 0 }));
                            }
                          }} 
                          className="w-3.5 h-3.5 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 cursor-pointer" 
                        />
                        Set Jam Ke
                      </label>
                    </div>
                    
                    {formData.startPeriod === 0 ? (
                      <div className="flex gap-2">
                        <div className="w-1/2 px-3 py-2.5 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 rounded-2xl font-black text-emerald-600 dark:text-emerald-400 flex justify-center items-center text-xs">
                          <span>JAM KE-0 (TAMBAHAN)</span>
                        </div>
                        <select 
                          name="class" 
                          value={formData.class} 
                          onChange={handleChange} 
                          className="w-1/2 px-3 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-slate-800 dark:text-white font-bold text-xs outline-none focus:ring-2 focus:ring-indigo-500/30 transition-all cursor-pointer"
                        >
                          <option value="">-- Kelas --</option>
                          {classes.map(c => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <div className="w-1/2 flex items-center gap-1 min-w-0">
                          <select 
                            name="startPeriod" 
                            value={formData.startPeriod} 
                            onChange={handleChange} 
                            className="w-1/2 px-1.5 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-slate-800 dark:text-white font-bold text-xs text-center outline-none focus:ring-2 focus:ring-indigo-500/30 transition-all cursor-pointer"
                          >
                            {Array.from({ length: 20 }, (_, i) => i + 1).map(p => (
                              <option key={p} value={p}>Jam {p}</option>
                            ))}
                          </select>
                          <span className="font-black text-slate-400 text-xs shrink-0">-</span>
                          <select 
                            name="endPeriod" 
                            value={formData.endPeriod} 
                            onChange={handleChange} 
                            className="w-1/2 px-1.5 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-slate-800 dark:text-white font-bold text-xs text-center outline-none focus:ring-2 focus:ring-indigo-500/30 transition-all cursor-pointer"
                          >
                            {Array.from({ length: 20 }, (_, i) => i + 1).map(p => (
                              <option key={p} value={p}>Jam {p}</option>
                            ))}
                          </select>
                        </div>
                        <select 
                          name="class" 
                          value={formData.class} 
                          onChange={handleChange} 
                          className="w-1/2 px-3 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-slate-800 dark:text-white font-bold text-xs outline-none focus:ring-2 focus:ring-indigo-500/30 transition-all cursor-pointer"
                        >
                          <option value="">-- Kelas --</option>
                          {classes.map(c => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="w-full px-4 py-3 bg-gradient-to-r from-indigo-50 to-violet-50 dark:from-indigo-950/40 dark:to-violet-950/40 border border-indigo-200/70 dark:border-indigo-800/60 rounded-2xl font-black text-indigo-700 dark:text-indigo-300 flex justify-between items-center text-sm">
                    <span>JAM {formData.startPeriod === formData.endPeriod ? formData.startPeriod : `${formData.startPeriod}-${formData.endPeriod}`}</span>
                    <span className="px-2.5 py-0.5 bg-indigo-600 text-white rounded-lg text-xs">KELAS {formData.class}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div>
                <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase block mb-1.5 ml-1">Jenis Kegiatan</label>
                <select name="activityType" value={formData.activityType} onChange={handleChange} className="w-full px-4 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-slate-800 dark:text-white font-bold text-sm outline-none focus:ring-2 focus:ring-indigo-500/30 transition-all cursor-pointer">
                    {Object.values(ActivityType).filter(type => type !== ActivityType.KBM_DAN_QUIZ).map(type => (
                        <option key={type} value={type}>{type === ActivityType.SAS ? 'Sumatif Akhir' : type}</option>
                    ))}
                </select>
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase block mb-1.5 ml-1">Status Kegiatan</label>
                <select name="status" value={formData.status} onChange={handleChange} className="w-full px-4 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-slate-800 dark:text-white font-bold text-sm outline-none focus:ring-2 focus:ring-indigo-500/30 transition-all cursor-pointer">
                    <option value="Selesai">✅ Selesai</option>
                    <option value="Belum Selesai">⏳ Belum Selesai</option>
                    <option value="Jam kosong">⚠️ Jam Kosong</option>
                </select>
              </div>
            </div>

            {(formData.activityType === ActivityType.KBM_DAN_QUIZ || formData.activityType === ActivityType.PEMBELAJARAN_DAN_ASESMEN) && (
              <div>
                <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase block mb-1.5 ml-1">Penilaian / Asesmen</label>
                <input 
                    type="text" 
                    name="penilaian" 
                    value={formData.penilaian || ''} 
                    onChange={handleChange} 
                    className="w-full px-4 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-slate-800 dark:text-white font-bold text-sm outline-none focus:ring-2 focus:ring-indigo-500/30 transition-all" 
                    placeholder="Masukkan jenis penilaian / asesmen..."
                />
              </div>
            )}

            {(formData.activityType === ActivityType.TUGAS_MANDIRI || formData.activityType === ActivityType.SAS) && (
              <div className="flex items-center gap-2 pt-1">
                  <input type="checkbox" name="isAttendanceOptional" checked={formData.isAttendanceOptional} onChange={handleCheckboxChange} className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 cursor-pointer" />
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300 cursor-pointer">Tidak perlu mengisi absensi untuk kegiatan ini</label>
              </div>
            )}
          </div>

          {/* Card 2: Materi & Tujuan Pembelajaran */}
          <div className="p-4 sm:p-5 bg-slate-50/80 dark:bg-slate-800/40 rounded-3xl border border-slate-200/60 dark:border-slate-700/50 space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-slate-200/60 dark:border-slate-700/50">
              <Target className="text-emerald-600 dark:text-emerald-400" size={16} />
              <span className="text-xs font-black text-slate-600 dark:text-slate-300 uppercase tracking-wider">Materi & Tujuan Pembelajaran</span>
            </div>

            <div className={formData.status === 'Jam kosong' ? 'opacity-40 grayscale pointer-events-none' : ''}>
              <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase block mb-1.5 ml-1">Tujuan Pembelajaran (TP)</label>
              <select name="tpId" value={formData.tpId} onChange={handleChange} className="w-full px-4 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-slate-800 dark:text-white font-bold text-sm outline-none focus:ring-2 focus:ring-indigo-500/30 transition-all cursor-pointer">
                <option value="">-- Pilih TP (Opsional) --</option>
                {objectives.map(obj => (
                  <option key={obj.id} value={obj.id}>{obj.code}: {obj.description.substring(0, 60)}...</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase block mb-1.5 ml-1">
                  {formData.status === 'Jam kosong' ? 'Aktivitas Alternatif' : 'Topik / Materi Pokok'} <span className="text-rose-500">*</span>
              </label>
              <input 
                  type="text" 
                  name="topic" 
                  value={formData.topic} 
                  onChange={handleChange} 
                  className="w-full px-4 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-slate-800 dark:text-white font-bold text-sm outline-none focus:ring-2 focus:ring-indigo-500/30 transition-all" 
                  placeholder={formData.status === 'Jam kosong' ? 'Misal: Belajar Mandiri' : 'Tuliskan topik / materi pembelajaran hari ini'}
              />
            </div>

            <div>
              <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase block mb-1.5 ml-1">Link Materi / Bahan Ajar (Opsional)</label>
              <div className="relative">
                <input 
                    type="url" 
                    name="materialLink" 
                    value={formData.materialLink || ''} 
                    onChange={handleChange} 
                    className="w-full pl-10 pr-4 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-slate-800 dark:text-white font-bold text-sm outline-none focus:ring-2 focus:ring-indigo-500/30 transition-all" 
                    placeholder="https://drive.google.com/..."
                />
                <LinkIcon size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Card 3: Catatan & Dokumentasi */}
          <div className="p-4 sm:p-5 bg-slate-50/80 dark:bg-slate-800/40 rounded-3xl border border-slate-200/60 dark:border-slate-700/50 space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-slate-200/60 dark:border-slate-700/50">
              <MessageSquare className="text-purple-600 dark:text-purple-400" size={16} />
              <span className="text-xs font-black text-slate-600 dark:text-slate-300 uppercase tracking-wider">Catatan & Dokumentasi Foto</span>
            </div>

            <div>
              <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase block mb-1.5 ml-1">Catatan Mengajar / Refleksi</label>
              <textarea name="notes" value={formData.notes} onChange={handleChange} rows={2} className="w-full px-4 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-slate-800 dark:text-white font-bold text-sm outline-none focus:ring-2 focus:ring-indigo-500/30 transition-all resize-none" placeholder={formData.status === 'Jam kosong' ? 'Sebutkan alasan jam kosong...' : 'Catatan kejadian khusus atau perkembangan KBM...'}></textarea>
            </div>

            <div>
              <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase block mb-1.5 ml-1">Dokumentasi Foto KBM (Opsional)</label>
              <div className="flex items-center gap-4">
                <label className="cursor-pointer flex items-center justify-center w-full px-4 py-3.5 bg-white dark:bg-slate-900 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-2xl text-slate-700 dark:text-slate-200 font-bold text-sm hover:bg-slate-100/80 dark:hover:bg-slate-800 transition-all">
                  <input 
                    type="file" 
                    accept="image/*" 
                    className="hidden" 
                    onChange={handleImageUpload}
                    disabled={isUploading}
                  />
                  {isUploading ? (
                    <span className="flex items-center gap-2 text-indigo-500"><Loader2 size={16} className="animate-spin" /> Mengunggah Gambar...</span>
                  ) : formData.documentationUrl ? (
                    <span className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400"><CheckCircle2 size={16} /> Foto Terunggah (Klik untuk Ganti)</span>
                  ) : (
                    <span className="flex items-center gap-2 text-slate-500 dark:text-slate-400"><Upload size={16} /> Unggah Foto Kegiatan</span>
                  )}
                </label>
              </div>

              {formData.documentationUrl && (
                <div className="mt-3 relative rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700 w-full h-36 group">
                  <img src={formData.documentationUrl} alt="Dokumentasi" className="w-full h-full object-cover" />
                  
                  {showDeleteImageConfirm ? (
                    <div className="absolute inset-0 bg-slate-900/85 backdrop-blur-xs flex flex-col items-center justify-center p-4 text-center animate-fade-in">
                      <p className="text-white text-xs font-bold mb-3">Hapus foto dokumentasi ini?</p>
                      <div className="flex gap-2">
                        <button 
                          onClick={() => setShowDeleteImageConfirm(false)}
                          className="px-3.5 py-1.5 bg-slate-700 text-white rounded-xl text-xs font-bold hover:bg-slate-600 transition-colors"
                        >
                          Batal
                        </button>
                        <button 
                          onClick={handleDeleteImage}
                          className="px-3.5 py-1.5 bg-rose-500 text-white rounded-xl text-xs font-bold hover:bg-rose-600 transition-colors"
                        >
                          Hapus
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button 
                      onClick={() => setShowDeleteImageConfirm(true)}
                      className="absolute top-2.5 right-2.5 p-2 bg-rose-500/90 hover:bg-rose-600 text-white rounded-xl shadow-md transition-all cursor-pointer"
                      title="Hapus Foto"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

        </div>

        {/* Footer Actions */}
        <div className="flex justify-end gap-3 pt-5 mt-2 border-t border-slate-200/60 dark:border-slate-800 shrink-0">
          <button 
            onClick={onClose} 
            className="px-6 py-3 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 font-extrabold rounded-2xl transition-all uppercase tracking-wider text-xs cursor-pointer"
          >
            Batal
          </button>
          <button 
            onClick={handleSave} 
            className="px-8 py-3 bg-gradient-to-r from-indigo-600 via-indigo-500 to-teal-600 text-white font-black rounded-2xl shadow-lg shadow-indigo-500/25 active:scale-95 hover:shadow-indigo-500/40 transition-all uppercase tracking-wider text-xs cursor-pointer flex items-center gap-2"
          >
            <CheckCircle2 size={16} />
            <span>Simpan Agenda</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const AgendaDetailModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  agenda: AgendaEntry | null;
  objectives: LearningObjective[];
  students: Student[];
  attendanceRecords: AttendanceRecord[];
}> = ({ isOpen, onClose, agenda, objectives, students, attendanceRecords }) => {
  if (!isOpen || !agenda) return null;

  const tp = objectives.find(o => o.id === agenda.tpId);
  const records = attendanceRecords.filter(r => r.agendaId === agenda.id);
  const presentCount = records.filter(r => r.status === AttendanceStatus.Hadir).length;
  const totalStudents = students.filter(s => s.class === agenda.class && s.status === 'Aktif').length;
  
  const isNoAbsenceActivity = agenda.class 
    ? !!agenda.isAttendanceOptional 
    : ([ActivityType.KEGIATAN_LOMBA, ActivityType.KEGIATAN_KESISWAAN, ActivityType.KEGIATAN_SEKOLAH, ActivityType.SAS, ActivityType.PERWALIAN].includes(agenda.activityType as any) || agenda.isAttendanceOptional);

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-[2px] z-[200] flex justify-center items-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl w-full max-w-2xl p-6 sm:p-8 relative animate-fade-in-up border border-slate-100 dark:border-slate-800 max-h-[95vh] flex flex-col">
        <button onClick={onClose} className="absolute top-6 right-6 text-slate-400 hover:text-slate-600 z-10 transition-colors"><X size={24} /></button>
        <h3 className="text-2xl font-black text-slate-800 dark:text-white mb-6 shrink-0 tracking-tight">Detail Agenda</h3>
        
        <div className="space-y-4 overflow-y-auto custom-scrollbar pr-2 flex-1 pb-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] font-black text-slate-500 uppercase">Tanggal</p>
              <p className="font-bold text-slate-800 dark:text-white">{safeFormatDate(agenda.date, { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}</p>
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-500 uppercase">Waktu / Kelas</p>
              <p className="font-bold text-slate-800 dark:text-white">Jam {agenda.startPeriod === 0 ? '0 (Tambahan)' : `${agenda.startPeriod}-${agenda.endPeriod}`} • Kelas {agenda.class || '-'}</p>
            </div>
          </div>
          
          <div>
            <p className="text-[10px] font-black text-slate-500 uppercase">Mata Pelajaran</p>
            <p className="font-bold text-slate-800 dark:text-white">{agenda.subject}</p>
          </div>

          <div>
            <p className="text-[10px] font-black text-slate-500 uppercase">Topik / Materi</p>
            <p className="font-bold text-slate-800 dark:text-white">{agenda.topic || '-'}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] font-black text-slate-500 uppercase">Jenis Kegiatan</p>
              <p className="font-bold text-slate-800 dark:text-white">{agenda.activityType}</p>
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-500 uppercase">Status</p>
              <p className="font-bold text-slate-800 dark:text-white">{agenda.status}</p>
            </div>
          </div>

          {tp && (
            <div>
              <p className="text-[10px] font-black text-slate-500 uppercase">Tujuan Pembelajaran</p>
              <p className="font-bold text-slate-800 dark:text-white">{tp.code} - {tp.description}</p>
            </div>
          )}

          <div>
            <p className="text-[10px] font-black text-slate-500 uppercase">Catatan</p>
            <p className="font-bold text-slate-800 dark:text-white whitespace-pre-wrap">{agenda.notes || '-'}</p>
          </div>

          {agenda.class && !isNoAbsenceActivity && (
            <div>
              <p className="text-[10px] font-black text-slate-500 uppercase">Kehadiran</p>
              <p className="font-bold text-slate-800 dark:text-white">{presentCount} dari {totalStudents} siswa hadir</p>
            </div>
          )}

          {agenda.documentationUrl && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] font-black text-slate-500 uppercase">Dokumentasi</p>
                <a 
                  href={agenda.documentationUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[10px] font-black text-indigo-500 hover:text-indigo-600 flex items-center gap-1"
                >
                  <ExternalLink size={12} /> Lihat Penuh
                </a>
              </div>
              <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 w-full flex justify-center bg-slate-100 dark:bg-slate-800">
                <img 
                  src={agenda.documentationUrl} 
                  alt="Dokumentasi Pembelajaran" 
                  className="max-w-full h-auto max-h-[40vh] object-contain" 
                  loading="lazy"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const AgendaView: React.FC<Props> = ({ 
  agendas, 
  setAgendas, 
  schedules, 
  availableClasses, 
  teacherSubject, 
  objectives, 
  activeUser, 
  onNavigate, 
  students, 
  attendanceRecords, 
  onUpdateAttendance, 
  schoolSettings, 
  initialAgenda, 
  initialTab,
  appTheme = 'wzc-themes'
}) => {
  const currentTheme = useMemo(() => getThemeById(appTheme), [appTheme]);
  const [activeTab, setActiveTab] = useState<'input' | 'recap'>('input');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAgenda, setEditingAgenda] = useState<AgendaEntry | null>(null);
  const [viewingAgendaDetail, setViewingAgendaDetail] = useState<AgendaEntry | null>(null);

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
    if (initialAgenda) {
      setActiveTab('recap');
      setEditingAgenda(initialAgenda);
      setIsModalOpen(true);
    }
  }, [initialAgenda, initialTab]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  
  // State for Direct Attendance
  const [directAttendanceAgenda, setDirectAttendanceAgenda] = useState<AgendaEntry | null>(null);
  
  // State for Delete Confirmation
  const [agendaToDelete, setAgendaToDelete] = useState<AgendaEntry | null>(null);
  
  // States for Recap Filters
  const [recapClassFilter, setRecapClassFilter] = useState<string>('SEMUA');
  const [recapTpFilter, setRecapTpFilter] = useState<string>('SEMUA');
  const [recapStartDate, setRecapStartDate] = useState<string>('');
  const [recapEndDate, setRecapEndDate] = useState<string>('');
  const [recapMonth, setRecapMonth] = useState<string>('SEMUA');
  const [recapYear, setRecapYear] = useState<string>('SEMUA');
  const [recapTypeFilter, setRecapTypeFilter] = useState<string>('SEMUA');
  const [recapSortOrder, setRecapSortOrder] = useState<'desc' | 'asc'>('desc');
  const [recapViewMode, setRecapViewMode] = useState<'table' | 'grid'>('grid');
  const [showRecapDocumentation, setShowRecapDocumentation] = useState(false);
  const [cardSize, setCardSize] = useState<number>(7); // 1: Smallest (10 cols), 10: Largest (1 col)
  const [showArchived, setShowArchived] = useState<boolean>(false);
  
  const [suggestedSlot, setSuggestedSlot] = useState<{start: number, end: number, class: string, isExtra?: boolean} | undefined>();
  const [viewingMaterial, setViewingMaterial] = useState<{link: string, topic: string} | null>(null);
  const [copiedAgenda, setCopiedAgenda] = useState<Partial<AgendaEntry> | null>(null);

  const handleCopyAgenda = (entry: AgendaEntry) => {
    setCopiedAgenda({
      topic: entry.topic,
      activityType: entry.activityType,
      status: entry.status,
      tpId: entry.tpId,
      tpDescription: entry.tpDescription,
      notes: entry.notes,
      materialLink: entry.materialLink
    });
    if (activeTab === 'recap') {
      setActiveTab('input');
    }
  };

  const formatActivityType = (type: string | undefined | null) => {
    if (!type) return '-';
    if (type === ActivityType.SAS || type === 'SAS') return 'Sumatif Akhir';
    return type;
  };

  const getEmbeddableLink = (url: string) => {
    if (!url) return null;
    try {
      const urlObj = new URL(url);
      if (urlObj.hostname.includes('youtube.com') || urlObj.hostname.includes('youtu.be')) {
        const videoId = urlObj.searchParams.get('v') || urlObj.pathname.split('/').pop();
        return `https://www.youtube.com/embed/${videoId}`;
      }
      if (urlObj.hostname.includes('docs.google.com') || urlObj.hostname.includes('drive.google.com')) {
        if (url.includes('/edit')) {
          return url.replace('/edit', '/preview');
        }
        if (url.includes('/view')) {
          return url.replace('/view', '/preview');
        }
        if (url.includes('drive.google.com/file/d/')) {
          const match = url.match(/\/d\/(.*?)\//);
          if (match && match[1]) {
            return `https://drive.google.com/file/d/${match[1]}/preview`;
          }
        }
        return url;
      }
      if (urlObj.hostname.includes('canva.com') && url.includes('/view')) {
        return url;
      }
      return null;
    } catch {
      return null;
    }
  };

  const handlePrintAgenda = () => {
    if (sortedRecapAgendas.length === 0) {
      alert('Tidak ada data untuk dicetak.');
      return;
    }

    const dayNames: string[] = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
    const monthNames = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
    const selectedTp = recapTpFilter === 'NO_TP' ? null : objectives.find(o => o.id === recapTpFilter);
    const today = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Mohon izinkan pop-up untuk mencetak.');
      return;
    }

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Cetak Laporan Agenda</title>
        <style>
          body { font-family: 'Times New Roman', Times, serif; padding: 20px; color: #000; }
          .kop-container { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; text-align: center; }
          .kop-logo { width: 80px; height: 80px; object-fit: contain; }
          .kop-text { flex: 1; text-align: center; }
          .kop-text p { margin: 0; line-height: 1.2; }
          .kop-gov { font-size: 16px; font-weight: bold; text-transform: uppercase; }
          .kop-dept { font-size: 16px; font-weight: bold; text-transform: uppercase; }
          .kop-school { font-size: 24px; font-weight: bold; text-transform: uppercase; }
          .kop-address { font-size: 12px; font-style: italic; }
          .kop-additional { font-size: 12px; }
          .kop-line-1 { height: 3px; background-color: #000; width: 100%; margin-top: 10px; }
          .kop-line-2 { height: 1px; background-color: #000; width: 100%; margin-top: 2px; }
          
          .report-title { text-align: center; font-size: 16px; font-weight: bold; margin: 20px 0; text-transform: uppercase; }
          
          .meta-table { width: 100%; margin-bottom: 20px; font-size: 12px; }
          .meta-table td { padding: 2px 5px; vertical-align: top; }
          .meta-table td:first-child { width: 150px; }
          .meta-table td:nth-child(2) { width: 10px; }
          
          .data-table { width: 100%; border-collapse: collapse; font-size: 13.5px; margin-bottom: 30px; }
          .data-table th, .data-table td { border: 1px solid #000; padding: 8px 6px; text-align: left; }
          table.data-table th { background-color: #7c3aed; color: #ffffff; font-weight: bold; text-align: center; }
          table.data-table td.left-align { text-align: left; }
          .bg-hatch-red {
            background: repeating-linear-gradient(45deg, #fee2e2, #fee2e2 5px, #fecaca 5px, #fecaca 10px) !important;
            -webkit-print-color-adjust: exact;
          }
          .bg-purple-100 { background-color: #f3e8ff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .bg-yellow-100 { background-color: #fef08a !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .bg-blue-100 { background-color: #dbeafe !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .bg-orange-100 { background-color: #ffedd5 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .bg-slate-100 { background-color: #f1f5f9 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          
          .signature-container { width: 100%; display: flex; justify-content: flex-end; margin-top: 40px; }
          .signature-box { width: 250px; text-align: center; font-size: 12px; }
          .signature-space { height: 80px; }
          .signature-name { font-weight: bold; text-decoration: underline; }
          
          @media print {
            @page { size: portrait; margin: 2cm 2cm 3cm 2cm; }
            body { padding: 0; }
          }
        </style>
      </head>
      <body>
        <!-- Kop Surat -->
        <div class="kop-container">
          <img src="${schoolSettings.departmentLogoUrl || 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c5/Logo_Kabupaten_Ciamis.png/800px-Logo_Kabupaten_Ciamis.png'}" class="kop-logo" alt="Logo Dinas" referrerPolicy="no-referrer" />
          <div class="kop-text">
            <p class="kop-gov">${schoolSettings.governmentName || 'PEMERINTAH KABUPATEN CIAMIS'}</p>
            <p class="kop-dept">${schoolSettings.departmentName || 'DINAS PENDIDIKAN'}</p>
            <p class="kop-school">${schoolSettings.schoolName || 'SMP NEGERI 1 KAWALI'}</p>
            <p class="kop-address">${schoolSettings.address || 'Jl. Raya Kawali No. 1, Kawali, Ciamis, Jawa Barat 46253'}</p>
            ${schoolSettings.additionalKopLine ? `<p class="kop-additional">${schoolSettings.additionalKopLine}</p>` : ''}
          </div>
          <img src="${schoolSettings.logoUrl || 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9c/Logo_Kemdikbud.png/800px-Logo_Kemdikbud.png'}" class="kop-logo" alt="Logo Sekolah" referrerPolicy="no-referrer" />
        </div>
        <div class="kop-line-1"></div>
        <div class="kop-line-2"></div>

        <div class="report-title">REKAPITULASI AGENDA MENGAJAR GURU</div>

        <table class="meta-table">
          <tr>
            <td>Mata Pelajaran</td><td>:</td><td>${teacherSubject}</td>
            <td>Kelas</td><td>:</td><td>${recapClassFilter === 'SEMUA' ? 'Semua Kelas' : `Kelas ${recapClassFilter}`}</td>
          </tr>
          <tr>
            <td>Nama Guru</td><td>:</td><td>${activeUser.name}</td>
            ${recapTpFilter !== 'SEMUA' ? `<td>Tujuan Pembelajaran</td><td>:</td><td>${recapTpFilter === 'NO_TP' ? 'Tidak Memiliki TP' : (selectedTp ? `${selectedTp.code} - ${selectedTp.description}` : '-')}</td>` : '<td></td><td></td><td></td>'}
          </tr>
        </table>

        <table class="data-table">
          <thead>
            <tr>
              <th>No</th>
              <th>Hari, Tanggal</th>
              <th>Jam Ke</th>
              <th>Kelas</th>
              <th>Topik / Materi</th>
              <th>Kode TP</th>
              <th>Jenis Kegiatan</th>
              <th>Status</th>
              <th>Catatan</th>
              ${showRecapDocumentation ? '<th>Dokumentasi</th>' : ''}
            </tr>
          </thead>
          <tbody>
            ${sortedRecapAgendas.map((entry, idx) => {
              const tp = objectives.find(o => o.id === entry.tpId);
              const dateObj = new Date(entry.date);
              const dayName = dayNames[dateObj.getDay()];
              return `
                <tr class="${entry.status === 'Jam kosong' ? 'bg-hatch-red' : (entry.activityType === ActivityType.ADMINISTRASI ? 'bg-purple-100' : (entry.activityType === ActivityType.TUGAS_MANDIRI ? 'bg-yellow-100' : (entry.activityType === ActivityType.KEGIATAN_LOMBA ? 'bg-orange-100' : (entry.activityType === ActivityType.KEGIATAN_KESISWAAN ? 'bg-[#D0B8DE] text-[#4B305E]' : (entry.activityType === ActivityType.KEGIATAN_SEKOLAH ? 'bg-slate-100' : '')))))}">
                  <td style="text-align: center;">${idx + 1}</td>
                  <td>${dayName}, ${entry.date}</td>
                  <td style="text-align: center;">${entry.startPeriod === 0 ? 'Jam ke-0 (Tambahan)' : `${entry.startPeriod}-${entry.endPeriod}`}</td>
                  <td style="text-align: center;">${entry.class || '-'}</td>
                  <td>${entry.topic || (entry.status === 'Jam kosong' ? 'KBM KOSONG' : '-')}</td>
                  <td style="text-align: center;">${tp?.code || '-'}</td>
                  <td style="text-align: center;">${entry.activityType || '-'}</td>
                  <td style="text-align: center;">${entry.status}</td>
                  <td>${entry.notes || '-'}</td>
                  ${showRecapDocumentation ? `<td>${entry.documentationUrl ? `<img src="${entry.documentationUrl}" style="max-width: 100px; height: auto;" />` : '-'}</td>` : ''}
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>

        <!-- Analisis Section -->
        <div style="margin-top: 30px;">
          <h3 style="font-size: 14px; font-weight: bold; text-transform: uppercase; border-bottom: 2px solid #000; padding-bottom: 5px; margin-bottom: 15px;">Persentase Rekapitulasi Agenda</h3>
          <div style="display: flex; justify-content: flex-start;">
            <table class="data-table" style="width: 350px; margin-bottom: 0; margin-left: 0;">
              <thead>
                <tr>
                  <th colspan="2">Persentase Jenis Kegiatan</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style="width: 70%;">Persentase KBM</td>
                  <td style="text-align: center; font-weight: bold;">${analysisStats.kbmPercent}%</td>
                </tr>
                <tr>
                  <td>Persentase Sumatif</td>
                  <td style="text-align: center; font-weight: bold;">${analysisStats.sumatifPercent}%</td>
                </tr>
                <tr>
                  <td>Persentase Kegiatan Lain</td>
                  <td style="text-align: center; font-weight: bold;">${analysisStats.otherPercent}%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="signature-container">
          <div class="signature-box">
            <p>Kawali, ${today}</p>
            <p>Guru Mata Pelajaran</p>
            <div class="signature-space"></div>
            <p class="signature-name">${activeUser.name}</p>
          </div>
        </div>

        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              window.close();
            }, 500);
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const handleExportExcelWithPrincipal = async () => {
    if (sortedRecapAgendas.length === 0) {
      alert('Tidak ada data untuk diekspor.');
      return;
    }

    const dayNames: string[] = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
    const monthNames = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
    
    const selectedTp = recapTpFilter === 'NO_TP' ? null : objectives.find(o => o.id === recapTpFilter);
    const today = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Rekap_Agenda');
    worksheet.pageSetup = { paperSize: 9, orientation: 'portrait' };

    // Set column widths
    worksheet.columns = [
      { width: 5 },   // A: No
      { width: 15 },  // B: Hari, Tanggal
      { width: 10 },  // C: Jam Ke
      { width: 10 },  // D: Kelas
      { width: 30 },  // E: Topik / Materi
      { width: 15 },  // F: Kode TP
      { width: 15 },  // G: Jenis Kegiatan
      { width: 15 },  // H: Status Kegiatan
      { width: 40 }   // I: Catatan
    ];

    // 1. Prepare Header (Kop Sekolah)
    const addImage = async (url: string, col: number, row: number) => {
      try {
        let base64Data = '';
        let extension = 'png';
        let imgWidth = 80;
        let imgHeight = 80;
        
        if (url.startsWith('data:image')) {
          base64Data = url.split(',')[1];
          const mime = url.substring(5, url.indexOf(';'));
          extension = mime.split('/')[1] || 'png';
          
          await new Promise<void>((resolve) => {
            const img = new window.Image();
            img.onload = () => {
              imgWidth = img.width;
              imgHeight = img.height;
              resolve();
            };
            img.onerror = () => resolve();
            img.src = url;
          });
        } else {
          const response = await fetch(url);
          const blob = await response.blob();
          base64Data = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
            reader.readAsDataURL(blob);
          });
          extension = blob.type.split('/')[1] || 'png';
          
          await new Promise<void>((resolve) => {
            const img = new window.Image();
            img.onload = () => {
              imgWidth = img.width;
              imgHeight = img.height;
              URL.revokeObjectURL(img.src);
              resolve();
            };
            img.onerror = () => {
              URL.revokeObjectURL(img.src);
              resolve();
            };
            img.src = URL.createObjectURL(blob);
          });
        }

        const logoId = workbook.addImage({
          base64: base64Data,
          extension: extension as any,
        });
        
        const targetHeight = 70;
        const targetWidth = (imgWidth / imgHeight) * targetHeight;
        
        worksheet.addImage(logoId, {
          tl: { col: col, row: row },
          ext: { width: targetWidth, height: targetHeight }
        });
      } catch (error) {
        console.error('Failed to add logo to excel:', error);
      }
    };

    if (schoolSettings.departmentLogoUrl) {
      await addImage(schoolSettings.departmentLogoUrl, 0.2, 0.2);
    }
    if (schoolSettings.logoUrl) {
      // Position school logo symmetrically on the right
      await addImage(schoolSettings.logoUrl, 8.1, 0.2);
    }

    worksheet.mergeCells('B1:H1');
    worksheet.getCell('B1').value = schoolSettings.governmentName || 'PEMERINTAH KABUPATEN CIAMIS';
    worksheet.getCell('B1').font = { bold: true, size: 12 };
    worksheet.getCell('B1').alignment = { horizontal: 'center' };

    worksheet.mergeCells('B2:H2');
    worksheet.getCell('B2').value = schoolSettings.departmentName || 'DINAS PENDIDIKAN';
    worksheet.getCell('B2').font = { bold: true, size: 12 };
    worksheet.getCell('B2').alignment = { horizontal: 'center' };

    worksheet.mergeCells('B3:H3');
    worksheet.getCell('B3').value = schoolSettings.schoolName.toUpperCase();
    worksheet.getCell('B3').font = { bold: true, size: 16 };
    worksheet.getCell('B3').alignment = { horizontal: 'center' };

    // Handle address - split into two lines if it contains a newline
    const address = schoolSettings.address || '';
    const addressParts = address.split('\n');
    
    if (addressParts.length >= 2) {
      worksheet.mergeCells('B4:H4');
      worksheet.getCell('B4').value = addressParts[0];
      worksheet.getCell('B4').font = { italic: true, size: 10 };
      worksheet.getCell('B4').alignment = { horizontal: 'center' };

      worksheet.mergeCells('B5:H5');
      worksheet.getCell('B5').value = addressParts[1];
      worksheet.getCell('B5').font = { italic: true, size: 10 };
      worksheet.getCell('B5').alignment = { horizontal: 'center' };
      
      // Line on row 6
      worksheet.mergeCells('A6:I6');
      worksheet.getCell('A6').border = { bottom: { style: 'thick' } };
    } else {
      worksheet.mergeCells('B4:H4');
      worksheet.getCell('B4').value = address;
      worksheet.getCell('B4').font = { italic: true, size: 10 };
      worksheet.getCell('B4').alignment = { horizontal: 'center' };

      // Line on row 5
      worksheet.mergeCells('A5:I5');
      worksheet.getCell('A5').border = { bottom: { style: 'thick' } };
    }

    // Title
    worksheet.mergeCells('A8:I8');
    worksheet.getCell('A8').value = 'REKAPITULASI AGENDA MENGAJAR GURU';
    worksheet.getCell('A8').font = { bold: true, size: 14 };
    worksheet.getCell('A8').alignment = { horizontal: 'center' };

    // Meta Data
    worksheet.getCell('A10').value = 'Mata Pelajaran';
    worksheet.getCell('B10').value = ': ' + teacherSubject;
    worksheet.getCell('D10').value = 'Kelas';
    worksheet.getCell('E10').value = ': ' + (recapClassFilter === 'SEMUA' ? 'Semua Kelas' : `Kelas ${recapClassFilter}`);

    worksheet.getCell('A11').value = 'Nama Guru';
    worksheet.getCell('B11').value = ': ' + activeUser.name;
    worksheet.getCell('D11').value = 'Periode';
    worksheet.getCell('E11').value = ': ' + (recapMonth !== 'SEMUA' ? `${monthNames[parseInt(recapMonth)-1]} ${recapYear}` : (recapStartDate || recapEndDate ? `${recapStartDate || 'Awal'} s/d ${recapEndDate || 'Sekarang'}` : 'Semua Waktu'));

    if (recapTpFilter !== 'SEMUA') {
      worksheet.getCell('A12').value = 'Tujuan Pembelajaran';
      worksheet.getCell('B12').value = ': ' + (recapTpFilter === 'NO_TP' ? 'Tidak Memiliki TP' : (selectedTp ? `${selectedTp.code} - ${selectedTp.description}` : '-'));
    }

    // Table Header
    const tableHeaderRow = 14;
    const headers = ['No', 'Hari, Tanggal', 'Jam Ke', 'Kelas', 'Topik / Materi', 'Kode TP', 'Jenis Kegiatan', 'Status Kegiatan', 'Catatan'];
    
    headers.forEach((header, index) => {
      const cell = worksheet.getCell(tableHeaderRow, index + 1);
      cell.value = header;
      cell.font = { bold: true };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF7C3AED' }
      };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    });

    // Table Data
    let currentRow = tableHeaderRow + 1;
    sortedRecapAgendas.forEach((entry, idx) => {
      const tp = objectives.find(o => o.id === entry.tpId);
      const dateObj = new Date(entry.date);
      const dayName = dayNames[dateObj.getDay()];
      
      const rowData = [
        idx + 1,
        `${dayName}, ${entry.date}`,
        entry.startPeriod === 0 ? 'Jam ke-0 (Tambahan)' : `${entry.startPeriod}-${entry.endPeriod}`,
        entry.class || '-',
        entry.topic || (entry.status === 'Jam kosong' ? 'KBM KOSONG' : '-'),
        tp?.code || '-',
        entry.activityType || '-',
        entry.status || '-',
        entry.notes || '-'
      ];

      rowData.forEach((data, index) => {
        const cell = worksheet.getCell(currentRow, index + 1);
        cell.value = data;
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
        cell.alignment = { vertical: 'top', wrapText: true };
        if (index === 0 || index === 2 || index === 3 || index === 5 || index === 6 || index === 7) {
          cell.alignment.horizontal = 'center';
        }

        if (entry.status === 'Jam kosong') {
          cell.fill = {
            type: 'pattern',
            pattern: 'darkGrid',
            fgColor: { argb: 'FFFFCCCC' },
            bgColor: { argb: 'FFFFEEEE' }
          };
        } else if (entry.activityType === ActivityType.ADMINISTRASI) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFE0E7FF' }
          };
        } else if (entry.activityType === ActivityType.TUGAS_MANDIRI) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFFEF08A' }
          };
        } else if (entry.activityType === ActivityType.KEGIATAN_LOMBA) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFFFEDD5' }
          };
        } else if (entry.activityType === ActivityType.KEGIATAN_KESISWAAN) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD0B8DE' } // Yellowish purple
          };
        } else if (entry.activityType === ActivityType.KEGIATAN_SEKOLAH) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF1F5F9' }
          };
        }
      });
      currentRow++;
    });

    // Signature
    const signatureRow = currentRow + 2;
    worksheet.getCell(`F${signatureRow}`).value = `Kawali, ${today}`;
    worksheet.getCell(`F${signatureRow + 1}`).value = 'Guru Mata Pelajaran';
    
    worksheet.getCell(`F${signatureRow + 5}`).value = activeUser.name;
    worksheet.getCell(`F${signatureRow + 5}`).font = { bold: true, underline: true };

    // Generate File
    const buffer = await workbook.xlsx.writeBuffer();
    const fileName = `Rekap_Agenda_${activeUser.name.replace(/\s+/g, '_')}_${recapClassFilter !== 'SEMUA' ? 'Kelas_' + recapClassFilter + '_' : ''}${new Date().toISOString().split('T')[0]}.xlsx`;
    saveAs(new Blob([buffer]), fileName);
  };

  const dayOfWeek = useMemo(() => {
    const d = new Date(selectedDate);
    const names: string[] = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
    return names[d.getDay()];
  }, [selectedDate]);

  const currentDayTimes = useMemo(() => {
    return schoolSettings.dayPeriodTimes?.[dayOfWeek] || schoolSettings.periodTimes;
  }, [schoolSettings, dayOfWeek]);

  const groupedTodaySchedules = useMemo(() => {
    const relevant = schedules
      .filter(s => s.day === dayOfWeek && !s.isBreak && s.subject !== 'Jam Istirahat' && s.class !== 'Istirahat')
      .sort((a, b) => a.period - b.period);
    const groups: { start: number, end: number, class: string, subject: string }[] = [];
    
    if (relevant.length === 0) return groups;
    
    let current = { 
        start: relevant[0].period, 
        end: relevant[0].period, 
        class: relevant[0].class, 
        subject: relevant[0].subject 
    };
    
    for (let i = 1; i < relevant.length; i++) {
        const s = relevant[i];
        if (s.period === current.end + 1 && s.class === current.class && s.subject === current.subject) {
            current.end = s.period;
        } else {
            groups.push(current);
            current = { 
                start: s.period, 
                end: s.period, 
                class: s.class, 
                subject: s.subject 
            };
        }
    }
    groups.push(current);
    return groups;
  }, [schedules, dayOfWeek]);

  // REKAP LOGIC: Sort TERBARU ke TERLAMA
  const sortedRecapAgendas = useMemo(() => {
    let list = [...agendas];
    
    // Filter isArchived
    if (!showArchived) {
        list = list.filter(a => !a.isArchived);
    }

    // Filter by Teacher's own agendas (unless admin)
    const isTeacher = teacherSubject && teacherSubject.trim() !== "";
    if (isTeacher && activeUser) {
        list = list.filter(a => {
            const agTeacherId = a.teacherId;
            const agSubject = (a.subject || '').trim().toLowerCase();
            const agClass = (a.class || '').trim().toLowerCase();
            const teacherSubjectLower = teacherSubject.trim().toLowerCase();
            const assignedClassesNormalized = (availableClasses || []).map(c => c.trim().toLowerCase());

            if (agTeacherId && activeUser.id) {
                return agTeacherId === activeUser.id;
            }

            // Fallback for legacy agendas without teacherId
            const isSubjectMatch = agSubject && agSubject === teacherSubjectLower;
            const isClassMatch = agClass && (assignedClassesNormalized.includes(agClass) || agClass === 'semua kelas');
            return isSubjectMatch && isClassMatch;
        });
    }
    
    // Filter Kelas
    if (recapClassFilter !== 'SEMUA') {
        list = list.filter(a => a.class === recapClassFilter);
    }
    
    // Filter TP
    if (recapTpFilter === 'NO_TP') {
        list = list.filter(a => !a.tpId || a.tpId.trim() === '');
    } else if (recapTpFilter !== 'SEMUA') {
        list = list.filter(a => a.tpId === recapTpFilter);
    }

    // Filter Tanggal
    if (recapStartDate) {
        list = list.filter(a => a.date >= recapStartDate);
    }
    if (recapEndDate) {
        list = list.filter(a => a.date <= recapEndDate);
    }

    // Filter Bulan & Tahun
    if (recapMonth !== 'SEMUA') {
        list = list.filter(a => {
            const month = parseInt(a.date.split('-')[1]);
            return month.toString() === recapMonth;
        });
    }
    if (recapYear !== 'SEMUA') {
        list = list.filter(a => {
            const year = a.date.split('-')[0];
            return year === recapYear;
        });
    }

    // Filter Jenis Kegiatan
    if (recapTypeFilter !== 'SEMUA') {
        list = list.filter(a => a.activityType === recapTypeFilter);
    }

    // Sort
    return list.sort((a, b) => {
        const orderMult = recapSortOrder === 'desc' ? 1 : -1;
        const dateCompare = new Date(b.date).getTime() - new Date(a.date).getTime();
        if (dateCompare !== 0) return dateCompare * orderMult;
        // Jika tanggal sama, urutkan jam pelajaran dari yang terbesar
        return (b.startPeriod - a.startPeriod) * orderMult;
    });
  }, [agendas, recapClassFilter, recapTpFilter, recapStartDate, recapEndDate, recapMonth, recapYear, recapTypeFilter, recapSortOrder, showArchived, teacherSubject, availableClasses, activeUser]);

  const handleOpenModal = (entry: AgendaEntry | null = null, slot?: {start: number, end: number, class: string, isExtra?: boolean}) => {
    if (!entry && slot && copiedAgenda) {
      setEditingAgenda({
        id: '',
        date: selectedDate,
        class: slot.class,
        subject: teacherSubject,
        startPeriod: slot.start,
        endPeriod: slot.end,
        ...copiedAgenda
      } as AgendaEntry);
    } else {
      setEditingAgenda(entry);
    }
    setSuggestedSlot(slot);
    setIsModalOpen(true);
  };
  
  const handleSaveAgenda = (entry: AgendaEntry) => {
    const enrichedEntry: AgendaEntry = {
      ...entry,
      teacherId: entry.teacherId || activeUser?.id,
      teacherName: entry.teacherName || activeUser?.name,
      academicYear: entry.academicYear || schoolSettings?.academicYear || undefined,
      semester: entry.semester || schoolSettings?.semester || undefined,
      isArchived: entry.isArchived !== undefined ? entry.isArchived : false
    };
    if (editingAgenda && editingAgenda.id) {
      setAgendas(agendas.map(a => a.id === enrichedEntry.id ? enrichedEntry : a));
    } else {
      setAgendas([...agendas, enrichedEntry]);
    }
  };

  const handleDeleteAgenda = (entry: AgendaEntry) => {
    setAgendaToDelete(entry);
  };

  const confirmDeleteAgenda = async () => {
    if (agendaToDelete) {
      if (agendaToDelete.documentationUrl) {
        try {
          const urlParts = agendaToDelete.documentationUrl.split('/');
          let fileName = urlParts[urlParts.length - 1];
          fileName = fileName.split('?')[0];
          fileName = decodeURIComponent(fileName);

          if (fileName) {
            const response = await fetch(`/api/storage/PictureClass/${encodeURIComponent(fileName)}`, {
              method: 'DELETE',
            });
            
            if (!response.ok) {
              let errorData;
              const text = await response.text();
              try {
                errorData = text ? JSON.parse(text) : { error: `HTTP Error ${response.status}: ${response.statusText}` };
              } catch (e) {
                errorData = { error: `Failed to parse error response: ${text || 'Empty response'}` };
              }
              console.error('Failed to delete image from bucket during agenda deletion:', response.status, errorData);
              alert(`Gagal menghapus gambar di bucket: ${errorData.error || 'Akses ditolak (Cek Policy RLS)'}. Agenda tidak dihapus.`);
              setAgendaToDelete(null);
              return; // Stop here, don't delete agenda
            }
          }
        } catch (error) {
          console.error('Error removing image from bucket during agenda deletion:', error);
          alert(`Terjadi kesalahan jaringan saat menghapus gambar: ${error instanceof Error ? error.message : String(error)}. Agenda tidak dihapus.`);
          setAgendaToDelete(null);
          return; // Stop here, don't delete agenda
        }
      }

      setAgendas(agendas.filter(a => a.id !== agendaToDelete.id));
      // Hapus absensi terkait juga
      onUpdateAttendance(attendanceRecords.filter(r => r.agendaId !== agendaToDelete.id));
      setAgendaToDelete(null);
    }
  };

  const analysisStats = useMemo(() => {
    const total = sortedRecapAgendas.length;
    if (total === 0) return { kbmPercent: 0, sumatifPercent: 0, otherPercent: 0, kbmCount: 0, sumatifCount: 0, otherCount: 0 };
    
    const kbmCount = sortedRecapAgendas.filter(a => a.activityType === ActivityType.KBM || a.activityType === ActivityType.KBM_DAN_QUIZ || a.activityType === ActivityType.PEMBELAJARAN_DAN_ASESMEN).length;
    const sumatifCount = sortedRecapAgendas.filter(a => a.activityType === ActivityType.SUMATIF || a.activityType === ActivityType.SAS).length;
    const otherCount = total - kbmCount - sumatifCount;
    
    return {
      kbmPercent: Math.round((kbmCount / total) * 100),
      sumatifPercent: Math.round((sumatifCount / total) * 100),
      otherPercent: Math.round((otherCount / total) * 100),
      kbmCount,
      sumatifCount,
      otherCount
    };
  }, [sortedRecapAgendas]);

  const recordedAgendasForToday = useMemo(() => {
    let list = agendas.filter(a => a.date === selectedDate);
    
    const isTeacher = teacherSubject && teacherSubject.trim() !== "";
    if (isTeacher && activeUser) {
        list = list.filter(a => {
            const agTeacherId = a.teacherId;
            const agSubject = (a.subject || '').trim().toLowerCase();
            const agClass = (a.class || '').trim().toLowerCase();
            const teacherSubjectLower = teacherSubject.trim().toLowerCase();
            const assignedClassesNormalized = (availableClasses || []).map(c => c.trim().toLowerCase());

            if (agTeacherId && activeUser.id) {
                return agTeacherId === activeUser.id;
            }

            // Fallback for legacy agendas without teacherId
            const isSubjectMatch = agSubject && agSubject === teacherSubjectLower;
            const isClassMatch = agClass && (assignedClassesNormalized.includes(agClass) || agClass === 'semua kelas');
            return isSubjectMatch && isClassMatch;
        });
    }

    return list.sort((a, b) => a.startPeriod - b.startPeriod);
  }, [agendas, selectedDate, teacherSubject, availableClasses, activeUser]);

  return (
    <>
      <AgendaModal 
        isOpen={isModalOpen} 
        onClose={() => {
          setIsModalOpen(false);
          setSuggestedSlot(undefined);
          setEditingAgenda(null);
        }} 
        onSave={handleSaveAgenda} 
        entry={editingAgenda} 
        classes={availableClasses} 
        teacherSubject={teacherSubject}
        objectives={objectives}
        suggestedStartPeriod={suggestedSlot?.start}
        suggestedEndPeriod={suggestedSlot?.end}
        suggestedClass={suggestedSlot?.class}
        suggestedDate={selectedDate}
        isExtraAgendaParam={suggestedSlot?.isExtra}
      />

      <AgendaDetailModal
        isOpen={!!viewingAgendaDetail}
        onClose={() => setViewingAgendaDetail(null)}
        agenda={viewingAgendaDetail}
        objectives={objectives}
        students={students}
        attendanceRecords={attendanceRecords}
      />

      {directAttendanceAgenda && (
          <DirectAttendanceModal 
            isOpen={!!directAttendanceAgenda}
            onClose={() => setDirectAttendanceAgenda(null)}
            agenda={directAttendanceAgenda}
            students={students}
            records={attendanceRecords}
            onSave={onUpdateAttendance}
          />
      )}

      {agendaToDelete && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-[2px] z-[200] flex justify-center items-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl w-full max-w-sm p-8 relative animate-fade-in-up border border-slate-100 dark:border-slate-800 text-center">
            <div className="w-20 h-20 bg-rose-50 dark:bg-rose-500/10 text-rose-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <Trash2 size={40} />
            </div>
            <h3 className="text-xl font-black text-slate-800 dark:text-white mb-2">Hapus Agenda?</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 font-medium mb-8">
              Apakah Anda yakin ingin menghapus agenda kelas <span className="font-black text-indigo-600">{agendaToDelete.class}</span> topik <span className="font-black text-indigo-600">"{agendaToDelete.topic}"</span>? Tindakan ini tidak dapat dibatalkan.
            </p>
            <div className="flex gap-3">
              <button 
                onClick={() => setAgendaToDelete(null)} 
                className="flex-1 px-6 py-3 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 font-black rounded-2xl text-xs uppercase tracking-widest"
              >
                Batal
              </button>
              <button 
                onClick={confirmDeleteAgenda} 
                className="flex-1 px-6 py-3 bg-rose-600 text-white font-black rounded-2xl shadow-xl shadow-rose-500/20 text-xs uppercase tracking-widest"
              >
                Ya, Hapus
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="space-y-8 animate-fade-in pb-20">
        <div className="flex bg-slate-100/80 dark:bg-slate-900/80 p-1.5 rounded-2xl border border-slate-200/80 dark:border-slate-800/80 w-fit shadow-xs backdrop-blur-md">
            <button 
                onClick={() => setActiveTab('input')}
                className={`flex items-center gap-2.5 px-6 py-3 rounded-xl font-black text-xs uppercase tracking-wider transition-all cursor-pointer ${
                  activeTab === 'input' 
                    ? `bg-gradient-to-r ${currentTheme.activeNavGradient || currentTheme.accentGradient} text-white shadow-lg shadow-black/10 ring-2 ring-white/20` 
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                }`}
            >
                <LayoutList size={16} /> 
                <span>Input Agenda Harian</span>
            </button>
            <button 
                onClick={() => setActiveTab('recap')}
                className={`flex items-center gap-2.5 px-6 py-3 rounded-xl font-black text-xs uppercase tracking-wider transition-all cursor-pointer ${
                  activeTab === 'recap' 
                    ? `bg-gradient-to-r ${currentTheme.activeNavGradient || currentTheme.accentGradient} text-white shadow-lg shadow-black/10 ring-2 ring-white/20` 
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                }`}
            >
                <History size={16} /> 
                <span>Rekap Agenda</span>
            </button>
        </div>

        {activeTab === 'input' ? (
          <div className="space-y-8">
              {/* Hero / Focus Date Header Card */}
              <div className={`bg-gradient-to-br ${currentTheme.cardGradients[0] || 'from-slate-900 via-indigo-950 to-slate-900'} text-white p-6 sm:p-8 rounded-[2.5rem] shadow-xl relative overflow-hidden border border-white/10`}>
                  {/* Decorative background glow */}
                  <div className="absolute -right-16 -bottom-16 w-72 h-72 bg-white/10 rounded-full blur-3xl pointer-events-none" />
                  <div className="absolute left-1/2 top-0 -translate-x-1/2 w-96 h-32 bg-white/10 rounded-full blur-3xl pointer-events-none" />

                  <div className="relative z-10 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                      <div>
                          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 border border-white/30 text-white text-[10px] font-black uppercase tracking-widest mb-3 backdrop-blur-md">
                              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                              Sinkronisasi Jurnal Mengajar
                          </div>
                          <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white">
                              Fokus Agenda Hari Ini
                          </h2>
                          <p className="text-xs text-white/80 font-semibold mt-1">
                              Kelola jurnal kelas, ketercapaian TP, dan rekap absensi peserta didik secara efisien.
                          </p>
                      </div>

                      {/* Date Selector Controls */}
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 bg-white/10 backdrop-blur-md p-2.5 rounded-3xl border border-white/15 w-full lg:w-auto">
                          <div className="flex items-center gap-2 bg-slate-900/60 px-3.5 py-2 rounded-2xl border border-white/10 cursor-pointer" onClick={(e) => {
                              try {
                                  const input = e.currentTarget.querySelector('input[type="date"]');
                                  if (input) (input as HTMLInputElement).showPicker();
                              } catch (err) {}
                          }}>
                              <Calendar size={18} className="text-white/80 shrink-0" />
                              <input 
                                  type="date" 
                                  value={selectedDate}
                                  onChange={e => setSelectedDate(e.target.value)}
                                  onClick={(e) => {
                                      e.stopPropagation();
                                      try {
                                          e.currentTarget.showPicker();
                                      } catch (err) {}
                                  }}
                                  className="bg-transparent font-extrabold text-white outline-none cursor-pointer text-sm"
                              />
                          </div>

                          {/* Quick Date Shortcuts */}
                          <div className="flex items-center gap-1.5 justify-center">
                              <button 
                                  onClick={() => {
                                      const d = new Date(selectedDate);
                                      d.setDate(d.getDate() - 1);
                                      setSelectedDate(d.toISOString().split('T')[0]);
                                  }}
                                  className="px-3 py-2 bg-white/10 hover:bg-white/20 rounded-xl text-[10px] font-black uppercase tracking-wider text-white transition-all cursor-pointer"
                                  title="Hari Kemarin"
                              >
                                  Kemarin
                              </button>
                              <button 
                                  onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
                                  className={`px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer ${
                                      selectedDate === new Date().toISOString().split('T')[0]
                                          ? 'bg-white text-slate-900 font-extrabold shadow-md'
                                          : 'bg-white/10 hover:bg-white/20 text-white'
                                  }`}
                              >
                                  Hari Ini
                              </button>
                              <button 
                                  onClick={() => {
                                      const d = new Date(selectedDate);
                                      d.setDate(d.getDate() + 1);
                                      setSelectedDate(d.toISOString().split('T')[0]);
                                  }}
                                  className="px-3 py-2 bg-white/10 hover:bg-white/20 rounded-xl text-[10px] font-black uppercase tracking-wider text-white transition-all cursor-pointer"
                                  title="Hari Besok"
                              >
                                  Besok
                              </button>
                          </div>
                      </div>
                  </div>

                  {/* Quick Metrics Bar */}
                  <div className="mt-6 pt-6 border-t border-white/10 grid grid-cols-2 sm:grid-cols-3 gap-3 relative z-10">
                      <div className="bg-white/10 backdrop-blur-md p-3.5 rounded-2xl border border-white/10 flex items-center gap-3">
                          <div className="p-2.5 bg-white/20 text-white rounded-xl">
                              <Clock size={18} />
                          </div>
                          <div>
                              <p className="text-[10px] font-bold text-white/70 uppercase tracking-widest">Jadwal Mengajar</p>
                              <p className="text-base font-black text-white">{groupedTodaySchedules.length} Sesi Kelas</p>
                          </div>
                      </div>

                      <div className="bg-white/10 backdrop-blur-md p-3.5 rounded-2xl border border-white/10 flex items-center gap-3">
                          <div className="p-2.5 bg-white/20 text-white rounded-xl">
                              <CheckCircle2 size={18} />
                          </div>
                          <div>
                              <p className="text-[10px] font-bold text-white/70 uppercase tracking-widest">Sudah Tercatat</p>
                              <p className="text-base font-black text-white">{recordedAgendasForToday.length} Agenda</p>
                          </div>
                      </div>

                      <div className="col-span-2 sm:col-span-1 bg-white/10 backdrop-blur-md p-3.5 rounded-2xl border border-white/10 flex items-center gap-3">
                          <div className="p-2.5 bg-white/20 text-white rounded-xl">
                              <AlertCircle size={18} />
                          </div>
                          <div>
                              <p className="text-[10px] font-bold text-white/70 uppercase tracking-widest">Belum Terisi</p>
                              <p className="text-base font-black text-white">
                                  {Math.max(0, groupedTodaySchedules.length - recordedAgendasForToday.length)} Sesi
                              </p>
                          </div>
                      </div>
                  </div>
              </div>

              <div className="mb-12">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-3 ml-1">
                      <div className="flex items-center gap-2.5">
                          <div className="p-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl">
                              <Clock size={18} />
                          </div>
                          <div>
                              <h3 className="text-sm font-black text-slate-800 dark:text-slate-100 uppercase tracking-widest">
                                  Jadwal Mengajar Anda ({dayOfWeek.toUpperCase()})
                              </h3>
                              <p className="text-[10px] font-bold text-slate-400">Pilih sesi kelas di bawah untuk langsung mencatat jurnal KBM</p>
                          </div>
                      </div>
                      <button 
                          onClick={() => handleOpenModal(null, { start: 0, end: 0, class: '', isExtra: true })} 
                          className={`flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r ${currentTheme.activeNavGradient || currentTheme.accentGradient} text-white rounded-2xl font-black text-xs uppercase tracking-wider shadow-md shadow-black/10 active:scale-95 transition-all cursor-pointer`}
                      >
                          <PlusCircle size={15} /> 
                          <span>Agenda Tambahan (Jam 0)</span>
                      </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 sm:gap-4">
                      {groupedTodaySchedules.map((slot, idx) => {
                          const existingAgenda = recordedAgendasForToday.find(a => 
                              (a.startPeriod === slot.start && a.endPeriod === slot.end && a.class === slot.class) ||
                              (a.class === slot.class && a.startPeriod === slot.start)
                          );
                          const isRecorded = !!existingAgenda;
                          
                          const themes = [
                            { 
                              bg: 'bg-gradient-to-br from-indigo-600 via-indigo-700 to-violet-800 text-white shadow-md shadow-indigo-500/20',
                              btn: 'bg-white text-indigo-900 hover:bg-indigo-50 shadow-sm',
                              btnSecondary: 'bg-white/20 hover:bg-white/30 text-white border-white/30',
                            },
                            { 
                              bg: 'bg-gradient-to-br from-violet-600 via-purple-700 to-fuchsia-800 text-white shadow-md shadow-violet-500/20',
                              btn: 'bg-white text-purple-900 hover:bg-purple-50 shadow-sm',
                              btnSecondary: 'bg-white/20 hover:bg-white/30 text-white border-white/30',
                            },
                            { 
                              bg: 'bg-gradient-to-br from-emerald-600 via-teal-700 to-cyan-800 text-white shadow-md shadow-emerald-500/20',
                              btn: 'bg-white text-teal-900 hover:bg-teal-50 shadow-sm',
                              btnSecondary: 'bg-white/20 hover:bg-white/30 text-white border-white/30',
                            },
                            { 
                              bg: 'bg-gradient-to-br from-amber-500 via-orange-600 to-red-600 text-white shadow-md shadow-amber-500/20',
                              btn: 'bg-white text-amber-950 hover:bg-amber-50 shadow-sm',
                              btnSecondary: 'bg-white/20 hover:bg-white/30 text-white border-white/30',
                            },
                            { 
                              bg: 'bg-gradient-to-br from-rose-600 via-pink-600 to-rose-800 text-white shadow-md shadow-rose-500/20',
                              btn: 'bg-white text-rose-950 hover:bg-rose-50 shadow-sm',
                              btnSecondary: 'bg-white/20 hover:bg-white/30 text-white border-white/30',
                            },
                            { 
                              bg: 'bg-gradient-to-br from-blue-600 via-sky-600 to-indigo-700 text-white shadow-md shadow-blue-500/20',
                              btn: 'bg-white text-blue-950 hover:bg-blue-50 shadow-sm',
                              btnSecondary: 'bg-white/20 hover:bg-white/30 text-white border-white/30',
                            },
                          ];
                          const theme = themes[idx % themes.length];
                          const timeStart = currentDayTimes?.find((pt: any) => pt.period === slot.start)?.startTime;
                          const timeEnd = currentDayTimes?.find((pt: any) => pt.period === slot.end)?.endTime;

                          return (
                              <div 
                                key={`${slot.start}-${slot.class}-${idx}`} 
                                className={`rounded-2xl transition-all duration-200 p-4 relative overflow-hidden flex flex-col justify-between gap-3 group ${theme.bg} ${
                                  isRecorded 
                                    ? 'ring-2 ring-white/40 shadow-lg' 
                                    : 'hover:shadow-xl hover:-translate-y-0.5'
                                }`}
                              >
                                  {/* Subtle Background Pattern Circle */}
                                  <div className="absolute -right-6 -bottom-6 w-24 h-24 rounded-full bg-white/10 pointer-events-none blur-sm" />

                                  {/* Header Badges */}
                                  <div className="flex flex-wrap items-center justify-between gap-1.5 relative z-10">
                                      <div className="flex items-center gap-1.5">
                                          <span className="px-2.5 py-1 rounded-lg bg-black/20 backdrop-blur-md text-white text-[10px] font-black uppercase tracking-wider border border-white/20 flex items-center gap-1">
                                              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                                              Jam {slot.start === slot.end ? slot.start : `${slot.start}-${slot.end}`}
                                          </span>

                                          {timeStart && timeEnd && (
                                            <span className="px-2 py-1 rounded-lg text-[10px] font-semibold text-white/90 bg-black/15 backdrop-blur-md border border-white/15 flex items-center gap-1">
                                              <Clock size={10} className="text-white/80 shrink-0" />
                                              {timeStart}-{timeEnd}
                                            </span>
                                          )}
                                      </div>

                                      {isRecorded ? (
                                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-emerald-400 text-slate-950 text-[9px] font-black tracking-wider uppercase shadow-xs">
                                              <CheckCircle2 size={11} className="text-slate-950" />
                                              Terisi
                                          </span>
                                      ) : (
                                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/20 text-white/80 text-[9px] font-bold tracking-wider uppercase border border-white/15">
                                              Belum
                                          </span>
                                      )}
                                  </div>

                                  {/* Class & Subject Core Info */}
                                  <div className="flex items-center justify-between gap-2 relative z-10 my-0.5">
                                      <h4 className="text-xl sm:text-2xl font-black text-white tracking-tight drop-shadow-xs">
                                          KELAS {slot.class}
                                      </h4>
                                      <span className="px-2.5 py-1 rounded-xl text-[10px] font-black uppercase tracking-wider bg-white/20 backdrop-blur-md text-white border border-white/30 shadow-xs">
                                          {slot.subject}
                                      </span>
                                  </div>

                                  {/* Recorded Preview Content or Action */}
                                  {isRecorded && existingAgenda ? (
                                      <div className="pt-2 border-t border-white/20 space-y-2 relative z-10">
                                          <div className="bg-black/20 backdrop-blur-xs px-2.5 py-1.5 rounded-xl border border-white/15">
                                              <p className="text-[9px] font-bold text-white/70 uppercase tracking-wider">Materi</p>
                                              <p className="text-xs font-bold text-white truncate">
                                                  {existingAgenda.topic || 'Belum ada topik'}
                                              </p>
                                          </div>

                                          <div className="flex items-center justify-between gap-1.5 pt-0.5">
                                              <button 
                                                  onClick={() => setViewingAgendaDetail(existingAgenda)}
                                                  className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all flex items-center gap-1 cursor-pointer border ${theme.btnSecondary}`}
                                              >
                                                  <Eye size={12} />
                                                  <span>Detail</span>
                                              </button>

                                              <button 
                                                  onClick={() => handleOpenModal(existingAgenda)}
                                                  className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all flex items-center gap-1 cursor-pointer ${theme.btn}`}
                                              >
                                                  <Edit size={12} />
                                                  <span>Edit</span>
                                              </button>
                                          </div>
                                      </div>
                                  ) : (
                                      <div className="pt-2 flex items-center justify-between gap-2 border-t border-white/20 relative z-10">
                                          <span className="text-[10px] font-semibold text-white/80 italic">
                                              Siap diisi
                                          </span>
                                          <button 
                                              onClick={() => handleOpenModal(null, { start: slot.start, end: slot.end, class: slot.class })} 
                                              className={`px-3 py-1.5 rounded-xl font-black text-[11px] uppercase tracking-wider ${theme.btn} flex items-center gap-1 active:scale-95 transition-all cursor-pointer`}
                                          >
                                              <span>Catat</span>
                                              <ChevronRight size={13} className="group-hover:translate-x-0.5 transition-transform" />
                                          </button>
                                      </div>
                                  )}
                              </div>
                          );
                      })}
                  </div>
              </div>

              <div className="border-t border-slate-100 dark:border-slate-800 pt-10">
                  <div className="flex items-center gap-2 mb-8 ml-2">
                      <BookMarked className="text-indigo-600" size={18} />
                      <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest">DAFTAR AGENDA TERINPUT ({recordedAgendasForToday.length})</h3>
                  </div>

                  {/* Desktop Table View */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full table-fixed text-sm text-left border-separate border-spacing-y-3">
                      <thead>
                        <tr className="text-[10px] text-slate-400 uppercase tracking-[0.2em] font-black">
                          <th className="px-6 py-4 w-[15%]">Urutan Jam</th>
                          <th className="px-6 py-4 w-[20%]">Rombel / Mapel</th>
                          <th className="px-6 py-4 w-[35%]">Topik & Kegiatan</th>
                          <th className="px-6 py-4 w-[12%]">Status</th>
                          <th className="px-6 py-4 w-[18%] text-center">Aksi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recordedAgendasForToday.map(entry => (
                          <tr key={entry.id} className="group transition-all hover:translate-x-1">
                            <td className="px-6 py-6 bg-white dark:bg-slate-900 rounded-l-3xl border-y border-l border-slate-200 dark:border-slate-800 shadow-sm group-hover:shadow-md transition-all">
                              <div className="flex flex-col gap-1">
                                {entry.startPeriod === 0 ? (
                                  <span className="w-fit px-3 py-1.5 bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400 rounded-xl text-[10px] uppercase tracking-widest font-black">JAM KE-0 (TAMBAHAN)</span>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <Clock size={14} className="text-indigo-500" />
                                    <span className="font-black text-indigo-600 dark:text-indigo-400">
                                      JAM KE-{entry.startPeriod === entry.endPeriod ? entry.startPeriod : `${entry.startPeriod}-${entry.endPeriod}`}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-6 bg-white dark:bg-slate-900 border-y border-slate-200 dark:border-slate-800 shadow-sm group-hover:shadow-md transition-all">
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 rounded-full bg-indigo-500" />
                                  <p className="font-black text-slate-800 dark:text-white">{entry.class ? `KELAS ${entry.class}` : '-'}</p>
                                </div>
                                <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest pl-4">{entry.subject}</p>
                              </div>
                            </td>
                            <td className="px-6 py-6 bg-white dark:bg-slate-900 border-y border-slate-200 dark:border-slate-800 shadow-sm group-hover:shadow-md transition-all">
                              <div className="flex flex-col gap-2">
                                  <p className="text-slate-800 dark:text-white font-black leading-tight text-sm">
                                    {entry.topic || (entry.status === 'Jam kosong' ? 'KBM Kosong' : '-')}
                                  </p>
                                  {entry.notes && (
                                    <p className="text-slate-500 dark:text-slate-400 text-xs italic line-clamp-2">
                                      {entry.notes}
                                    </p>
                                  )}
                                  {entry.materialLink && (
                                      <button onClick={() => setViewingMaterial({link: entry.materialLink!, topic: entry.topic})} className="flex items-center gap-1.5 text-[10px] font-black text-indigo-500 hover:text-indigo-600 transition-colors w-fit">
                                          <ExternalLink size={12} /> LIHAT BAHAN AJAR
                                      </button>
                                  )}
                              </div>
                            </td>
                            <td className="px-6 py-6 bg-white dark:bg-slate-900 border-y border-slate-200 dark:border-slate-800 shadow-sm group-hover:shadow-md transition-all">
                              <span className={`inline-flex px-3 py-1.5 text-[10px] font-black rounded-xl uppercase tracking-widest ${
                                  entry.status === 'Selesai' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400' : 
                                  entry.status === 'Jam kosong' ? 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-400' : 
                                  'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400'
                              }`}>
                                  {entry.status}
                              </span>
                            </td>
                            <td className="px-6 py-6 bg-white dark:bg-slate-900 rounded-r-3xl border-y border-r border-slate-200 dark:border-slate-800 shadow-sm group-hover:shadow-md transition-all text-center">
                              <div className="flex justify-center gap-2">
                                <button onClick={() => handleCopyAgenda(entry)} className="p-2.5 bg-slate-50 dark:bg-slate-800 text-emerald-500 rounded-xl hover:bg-emerald-500 hover:text-white transition-all shadow-sm border border-slate-100 dark:border-slate-700" title="Salin Agenda"><Copy size={16}/></button>
                                <button onClick={() => handleOpenModal(entry)} className="p-2.5 bg-slate-50 dark:bg-slate-800 text-indigo-500 rounded-xl hover:bg-indigo-500 hover:text-white transition-all shadow-sm border border-slate-100 dark:border-slate-700" title="Edit Agenda"><Edit size={16}/></button>
                                {entry.class && !entry.isAttendanceOptional && (
                                  <button onClick={() => setDirectAttendanceAgenda(entry)} className="p-2.5 bg-slate-50 dark:bg-slate-800 text-cyan-500 rounded-xl hover:bg-cyan-500 hover:text-white transition-all shadow-sm border border-slate-100 dark:border-slate-700" title="Absensi"><UserCheck size={16}/></button>
                                )}
                                <button onClick={() => handleDeleteAgenda(entry)} className="p-2.5 bg-slate-50 dark:bg-slate-800 text-rose-500 rounded-xl hover:bg-rose-500 hover:text-white transition-all shadow-sm border border-slate-100 dark:border-slate-700" title="Hapus"><Trash2 size={16}/></button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="md:hidden space-y-4">
                    {recordedAgendasForToday.map(entry => (
                      <div key={entry.id} className="bg-white dark:bg-slate-900 p-5 rounded-[2rem] border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col gap-4">
                        <div className="flex justify-between items-start">
                          <div className="flex flex-col gap-1">
                            <span className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest">
                              {entry.startPeriod === 0 ? 'JAM KE-0 (TAMBAHAN)' : `JAM KE-${entry.startPeriod === entry.endPeriod ? entry.startPeriod : `${entry.startPeriod}-${entry.endPeriod}`}`}
                            </span>
                            <h4 className="font-black text-slate-800 dark:text-white text-base">
                              {entry.class ? `KELAS ${entry.class}` : '-'}
                            </h4>
                            <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">{entry.subject}</p>
                          </div>
                          <span className={`px-3 py-1 text-[10px] font-black rounded-xl uppercase tracking-widest ${
                              entry.status === 'Selesai' ? 'bg-green-100 text-green-700' : 
                              entry.status === 'Jam kosong' ? 'bg-rose-100 text-rose-700' : 
                              'bg-amber-100 text-amber-700'
                          }`}>
                              {entry.status}
                          </span>
                        </div>

                        <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-800">
                          <p className="text-slate-800 dark:text-white font-black text-sm leading-tight italic">
                            "{entry.topic || (entry.status === 'Jam kosong' ? 'KBM Kosong' : '-')}"
                          </p>
                          {entry.notes && (
                            <p className="text-slate-500 dark:text-slate-400 text-xs mt-1 italic">
                              {entry.notes}
                            </p>
                          )}
                          {entry.materialLink && (
                            <button onClick={() => setViewingMaterial({link: entry.materialLink!, topic: entry.topic})} className="mt-2 flex items-center gap-1 text-[10px] font-black text-indigo-500 hover:underline">
                              <ExternalLink size={12} /> Lihat Bahan Ajar
                            </button>
                          )}
                        </div>

                        <div className="flex justify-center gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                          <button onClick={() => handleCopyAgenda(entry)} className="flex-1 flex items-center justify-center gap-2 py-3 bg-slate-50 dark:bg-slate-800 text-emerald-500 rounded-xl font-black text-[10px] uppercase tracking-widest border border-slate-200 dark:border-slate-700 transition-all">
                            <Copy size={14}/> Salin
                          </button>
                          <button onClick={() => handleOpenModal(entry)} className="flex-1 flex items-center justify-center gap-2 py-3 bg-slate-50 dark:bg-slate-800 text-indigo-500 rounded-xl font-black text-[10px] uppercase tracking-widest border border-slate-200 dark:border-slate-700 transition-all">
                            <Edit size={14}/> Edit
                          </button>
                          {entry.class && !entry.isAttendanceOptional && (
                            <button onClick={() => setDirectAttendanceAgenda(entry)} className="flex-1 flex items-center justify-center gap-2 py-3 bg-slate-50 dark:bg-slate-800 text-cyan-500 rounded-xl font-black text-[10px] uppercase tracking-widest border border-slate-200 dark:border-slate-700 transition-all">
                              <UserCheck size={14}/> Absen
                            </button>
                          )}
                          <button onClick={() => handleDeleteAgenda(entry)} className="flex-1 flex items-center justify-center gap-2 py-3 bg-slate-50 dark:bg-slate-800 text-rose-500 rounded-xl font-black text-[10px] uppercase tracking-widest border border-slate-200 dark:border-slate-700 transition-all">
                            <Trash2 size={14}/> Hapus
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
              </div>
          </div>
        ) : (
          /* VIEW REKAP AGENDA DENGAN FILTER CANGGIH */
          <div className="bg-white dark:bg-slate-900 p-6 sm:p-8 rounded-[2.5rem] shadow-sm border border-slate-200 dark:border-slate-800">
              <div className="flex flex-col gap-6 mb-8">
                  <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
                      <div>
                          <h2 className="text-[21px] font-bold text-slate-900 dark:text-white tracking-tight leading-none mb-1">Rekapitulasi Agenda Mengajar</h2>
                          <p className="text-[9px] text-slate-400 font-black uppercase tracking-widest mt-1">Laporan Administratif Keseluruhan</p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-1 rounded-xl md:mr-2">
                          <button 
                            onClick={() => setRecapViewMode('table')}
                            className={`p-2 rounded-lg transition-all ${recapViewMode === 'table' ? `bg-gradient-to-r ${currentTheme.activeNavGradient || currentTheme.accentGradient} text-white shadow-sm` : 'text-slate-400 hover:text-slate-600'}`}
                            title="Mode Tabel"
                          >
                            <LayoutList size={18} />
                          </button>
                          <button 
                            onClick={() => setRecapViewMode('grid')}
                            className={`p-2 rounded-lg transition-all ${recapViewMode === 'grid' ? `bg-gradient-to-r ${currentTheme.activeNavGradient || currentTheme.accentGradient} text-white shadow-sm` : 'text-slate-400 hover:text-slate-600'}`}
                            title="Mode Thumbnail"
                          >
                            <CalendarDays size={18} />
                          </button>
                        </div>

                        {recapViewMode === 'grid' && (
                          <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 dark:bg-slate-800 rounded-xl md:mr-2">
                            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1 shrink-0">Ukuran</label>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-black text-slate-400">S</span>
                              <input 
                                type="range" 
                                min="1" 
                                max="10" 
                                step="1"
                                value={cardSize}
                                onChange={(e) => setCardSize(parseInt(e.target.value))}
                                className="w-20 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                              />
                              <span className="text-[10px] font-black text-slate-400">L</span>
                            </div>
                          </div>
                        )}

                        
                        <label className="flex items-center gap-2 px-3 py-2.5 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                            <input 
                              type="checkbox" 
                              checked={showRecapDocumentation}
                              onChange={(e) => setShowRecapDocumentation(e.target.checked)}
                              className="w-3 h-3 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500"
                            />
                            <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1">Sertakan Dokumentasi</span>
                        </label>

                        <button 
                          onClick={handlePrintAgenda}
                          className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white font-black rounded-xl hover:bg-indigo-700 shadow-lg shadow-indigo-500/20 transition-all text-[10px] tracking-widest uppercase"
                        >
                            <Printer size={14} /> Cetak
                        </button>
                        <button 
                          onClick={handleExportExcelWithPrincipal}
                          className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white font-black rounded-xl hover:bg-emerald-700 shadow-lg shadow-emerald-500/20 transition-all text-[10px] tracking-widest uppercase"
                        >
                            <FileSpreadsheet size={14} /> Export
                        </button>
                      </div>
                  </div>

                  {/* BARIS FILTER COMPACT */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-wrap items-stretch lg:items-center gap-3 p-4 bg-slate-50 dark:bg-slate-800/40 rounded-2xl border border-slate-100 dark:border-slate-800">
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700">
                          <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1 shrink-0"><Filter size={10}/> Kelas</label>
                          <select 
                            value={recapClassFilter}
                            onChange={(e) => setRecapClassFilter(e.target.value)}
                            className="bg-transparent border-none text-[10px] font-black text-indigo-600 dark:text-indigo-400 outline-none focus:ring-0 cursor-pointer w-full"
                          >
                            <option value="SEMUA">SEMUA</option>
                            {availableClasses.sort().map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                      </div>

                      <div className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700">
                          <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1 shrink-0"><Tags size={10}/> Kegiatan</label>
                          <select 
                            value={recapTypeFilter}
                            onChange={(e) => setRecapTypeFilter(e.target.value)}
                            className="bg-transparent border-none text-[10px] font-black text-indigo-600 dark:text-indigo-400 outline-none focus:ring-0 cursor-pointer w-full max-w-[120px]"
                          >
                            <option value="SEMUA">SEMUA</option>
                            {Object.values(ActivityType).filter(type => type !== ActivityType.KBM_DAN_QUIZ).map(type => (
                                <option key={type} value={type}>{type === ActivityType.SAS ? 'Sumatif Akhir' : type}</option>
                            ))}
                          </select>
                      </div>
                      
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700">
                          <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1 shrink-0"><Target size={10}/> TP</label>
                          <select 
                            value={recapTpFilter}
                            onChange={(e) => setRecapTpFilter(e.target.value)}
                            className="bg-transparent border-none text-[10px] font-black text-indigo-600 dark:text-indigo-400 outline-none focus:ring-0 cursor-pointer w-full max-w-[140px]"
                          >
                            <option value="SEMUA">SEMUA</option>
                            <option value="NO_TP">Tidak memiliki TP</option>
                            {objectives.map(o => <option key={o.id} value={o.id}>{o.code}</option>)}
                          </select>
                      </div>

                      <div className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700">
                          <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1 shrink-0"><CalendarDays size={10}/> Periode</label>
                          <div className="flex items-center gap-1 w-full justify-between">
                            <select 
                                value={recapMonth}
                                onChange={(e) => setRecapMonth(e.target.value)}
                                className="bg-transparent border-none text-[10px] font-black text-indigo-600 dark:text-indigo-400 outline-none focus:ring-0 cursor-pointer"
                            >
                                <option value="SEMUA">BULAN</option>
                                {['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'].map((m, i) => (
                                    <option key={m} value={(i + 1).toString()}>{m}</option>
                                ))}
                            </select>
                            <select 
                                value={recapYear}
                                onChange={(e) => setRecapYear(e.target.value)}
                                className="bg-transparent border-none text-[10px] font-black text-indigo-600 dark:text-indigo-400 outline-none focus:ring-0 cursor-pointer"
                            >
                                <option value="SEMUA">TAHUN</option>
                                {[2024, 2025, 2026, 2027].map(y => (
                                    <option key={y} value={y.toString()}>{y}</option>
                                ))}
                            </select>
                          </div>
                      </div>

                      <div className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 sm:col-span-2 lg:col-span-1">
                          <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1 shrink-0"><Clock size={10}/> Rentang</label>
                          <div className="flex items-center gap-1 w-full justify-between">
                              <input 
                                type="date" 
                                value={recapStartDate}
                                onChange={(e) => setRecapStartDate(e.target.value)}
                                className="bg-transparent border-none text-[10px] font-black text-indigo-600 dark:text-indigo-400 outline-none focus:ring-0 cursor-pointer w-full"
                              />
                              <span className="text-slate-300">-</span>
                              <input 
                                type="date" 
                                value={recapEndDate}
                                onChange={(e) => setRecapEndDate(e.target.value)}
                                className="bg-transparent border-none text-[10px] font-black text-indigo-600 dark:text-indigo-400 outline-none focus:ring-0 cursor-pointer w-full"
                              />
                          </div>
                      </div>

                      <div className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700">
                          <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1 shrink-0"><ArrowUpDown size={10}/> Urutkan</label>
                          <select 
                            value={recapSortOrder}
                            onChange={(e) => setRecapSortOrder(e.target.value as 'asc' | 'desc')}
                            className="bg-transparent border-none text-[10px] font-black text-indigo-600 dark:text-indigo-400 outline-none focus:ring-0 cursor-pointer w-full"
                          >
                            <option value="desc">Terbaru</option>
                            <option value="asc">Terlama</option>
                          </select>
                      </div>

                      <button 
                        onClick={() => setShowArchived(!showArchived)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-[10px] font-black uppercase tracking-widest transition-all ${
                          showArchived 
                            ? 'bg-amber-500/10 text-amber-600 border-amber-300 dark:border-amber-700/50' 
                            : 'bg-white dark:bg-slate-900 text-slate-500 border-slate-200 dark:border-slate-700 hover:bg-slate-50'
                        }`}
                        title="Tampilkan Agenda Terarsip"
                      >
                        <Archive size={12} />
                        <span>Arsip: {showArchived ? 'Tampil' : 'Sembunyi'}</span>
                      </button>

                      <button 
                        onClick={() => {
                            setRecapClassFilter('SEMUA');
                            setRecapTpFilter('SEMUA');
                            setRecapStartDate('');
                            setRecapEndDate('');
                            setRecapMonth('SEMUA');
                            setRecapYear('SEMUA');
                            setRecapTypeFilter('SEMUA');
                            setRecapSortOrder('desc');
                            setShowArchived(false);
                        }}
                        className="p-2 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg transition-colors ml-auto flex items-center justify-center"
                        title="Reset Filter"
                      >
                          <X size={14} /> <span className="lg:hidden text-[10px] font-black ml-1 uppercase">Reset</span>
                      </button>
                  </div>
              </div>
              
              {/* Analytics Dashboard Mini */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  <div className="bg-indigo-50/50 dark:bg-indigo-900/20 p-5 rounded-3xl border border-indigo-100 dark:border-indigo-800/30 shadow-sm shadow-indigo-100/50 dark:shadow-none">
                      <div className="flex justify-between items-start mb-2">
                        <p className="text-[10px] font-black text-indigo-500 dark:text-indigo-400 uppercase tracking-widest">Persentase KBM</p>
                        <BookMarked size={16} className="text-indigo-500 dark:text-indigo-400" />
                      </div>
                      <div className="flex items-end gap-2">
                          <p className="text-3xl font-black text-indigo-600 dark:text-indigo-400 leading-none">{analysisStats.kbmPercent}%</p>
                          <p className="text-[10px] font-bold text-indigo-500/70 dark:text-indigo-400 pb-1">({analysisStats.kbmCount} Agenda)</p>
                      </div>
                      <div className="w-full h-1.5 bg-indigo-100/70 dark:bg-indigo-900/40 rounded-full mt-4 overflow-hidden">
                          <motion.div 
                              initial={{ width: 0 }}
                              animate={{ width: `${analysisStats.kbmPercent}%` }}
                              className="h-full bg-indigo-500 rounded-full shadow-[0_0_8px_rgba(99,102,241,0.4)]"
                          />
                      </div>
                  </div>
                  
                  <div className="bg-emerald-50/50 dark:bg-emerald-900/20 p-5 rounded-3xl border border-emerald-100 dark:border-emerald-800/30 shadow-sm shadow-emerald-100/50 dark:shadow-none">
                      <div className="flex justify-between items-start mb-2">
                        <p className="text-[10px] font-black text-emerald-500 dark:text-emerald-400 uppercase tracking-widest">Persentase Sumatif</p>
                        <Target size={16} className="text-emerald-500 dark:text-emerald-400" />
                      </div>
                      <div className="flex items-end gap-2">
                          <p className="text-3xl font-black text-emerald-600 dark:text-emerald-400 leading-none">{analysisStats.sumatifPercent}%</p>
                          <p className="text-[10px] font-bold text-emerald-500/70 dark:text-emerald-400 pb-1">({analysisStats.sumatifCount} Agenda)</p>
                      </div>
                      <div className="w-full h-1.5 bg-emerald-100/70 dark:bg-emerald-900/40 rounded-full mt-4 overflow-hidden">
                          <motion.div 
                              initial={{ width: 0 }}
                              animate={{ width: `${analysisStats.sumatifPercent}%` }}
                              className="h-full bg-emerald-500 rounded-full shadow-[0_0_8px_rgba(16,185,129,0.4)]"
                          />
                      </div>
                  </div>

                  <div className="bg-amber-50/50 dark:bg-amber-900/20 p-5 rounded-3xl border border-amber-100 dark:border-amber-800/30 shadow-sm shadow-amber-100/50 dark:shadow-none">
                      <div className="flex justify-between items-start mb-2">
                        <p className="text-[10px] font-black text-amber-500 dark:text-amber-400 uppercase tracking-widest">Kegiatan Lain</p>
                        <Info size={16} className="text-amber-500 dark:text-amber-400" />
                      </div>
                      <div className="flex items-end gap-2">
                          <p className="text-3xl font-black text-amber-600 dark:text-amber-400 leading-none">{analysisStats.otherPercent}%</p>
                          <p className="text-[10px] font-bold text-amber-500/70 dark:text-amber-400 pb-1">({analysisStats.otherCount} Agenda)</p>
                      </div>
                      <div className="w-full h-1.5 bg-amber-100/70 dark:bg-amber-900/40 rounded-full mt-4 overflow-hidden">
                          <motion.div 
                              initial={{ width: 0 }}
                              animate={{ width: `${analysisStats.otherPercent}%` }}
                              className="h-full bg-amber-500 rounded-full shadow-[0_0_8px_rgba(245,158,11,0.4)]"
                          />
                      </div>
                  </div>
              </div>

              <div className="p-0 mt-6">
                <div>
                  {recapViewMode === 'table' ? (
                    <div className="overflow-x-auto custom-scrollbar">
                      <table className="w-full table-fixed text-xs text-left border-separate border-spacing-y-2">
                        <thead>
                          <tr className="text-[10px] text-slate-400 uppercase tracking-widest font-black">
                            <th className="px-1 py-2 text-center w-8">No</th>
                            <th className="px-2 py-2 w-[10%]">Tanggal/Jam</th>
                            <th className="px-2 py-2 w-[12%]">Kelas/Mapel</th>
                            <th className="px-2 py-2 w-auto">Materi</th>
                            <th className="px-1 py-2 text-center w-[6%]">TP</th>
                            <th className="px-1 py-2 text-center w-[6%]">Tugas</th>
                            <th className="px-1 py-2 text-center w-[8%]">Jenis</th>
                            <th className="px-1 py-2 text-center w-[8%]">Status</th>
                            <th className="px-1 py-2 text-center w-[10%]">Aksi</th>
                            <th className="px-1 py-2 text-center w-[10%]">Absensi</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedRecapAgendas.map((entry, idx) => {
                            const isTask = [ActivityType.KBM_DAN_QUIZ, ActivityType.SUMATIF, ActivityType.SAS].includes(entry.activityType || ActivityType.KBM);
                            const tp = objectives.find(o => o.id === entry.tpId);
                            
                            const agendaRecords = attendanceRecords.filter(r => r.agendaId === entry.id);
                            const presentCount = agendaRecords.filter(r => r.status === AttendanceStatus.Hadir).length;
                            const totalStudents = students.filter(s => s.class === entry.class && s.status === 'Aktif').length;

                            const rowBgClass = entry.activityType === ActivityType.ADMINISTRASI
                              ? 'bg-purple-50 dark:bg-purple-900/40 border-purple-100 dark:border-purple-800'
                              : (entry.activityType === ActivityType.SUMATIF || entry.activityType === ActivityType.SAS)
                              ? 'bg-sky-50 dark:bg-sky-900/40 border-sky-100 dark:border-sky-800'
                              : entry.activityType === ActivityType.TUGAS_MANDIRI
                              ? 'bg-yellow-50 dark:bg-yellow-900/40 border-yellow-100 dark:border-yellow-800'
                              : entry.activityType === ActivityType.KEGIATAN_LOMBA
                              ? 'bg-orange-50 dark:bg-orange-900/40 border-orange-100 dark:border-orange-800'
                              : entry.activityType === ActivityType.KEGIATAN_KESISWAAN
                              ? 'bg-fuchsia-50 dark:bg-[#685275]/40 border-fuchsia-100 dark:border-[#523B5E] text-fuchsia-700 dark:text-[#E3D3EB]'
                              : entry.activityType === ActivityType.KEGIATAN_SEKOLAH
                              ? 'bg-slate-50 dark:bg-slate-800 border-slate-100 dark:border-slate-700'
                              : entry.status === 'Jam kosong' 
                              ? 'bg-rose-50 dark:bg-rose-900/40 border-rose-100 dark:border-rose-800' 
                              : entry.status === 'Belum Selesai'
                              ? 'bg-amber-50 dark:bg-amber-900/40 border-amber-100 dark:border-amber-800'
                              : entry.status === 'Selesai'
                              ? 'bg-emerald-50 dark:bg-emerald-900/40 border-emerald-100 dark:border-emerald-800'
                              : 'bg-white dark:bg-slate-900 border-slate-50 dark:border-slate-800';

                            return (
                              <tr key={entry.id} className="group transition-all cursor-pointer hover:shadow-sm" onClick={() => setViewingAgendaDetail(entry)}>
                                <td className={`px-1 py-2 ${rowBgClass} rounded-l-xl border-y border-l font-black text-slate-400 text-center`}>
                                  {idx + 1}
                                </td>
                                <td className={`px-2 py-2 ${rowBgClass} border-y`}>
                                  <p className="font-bold text-slate-600 dark:text-slate-300">{safeFormatDate(entry.date, { day: '2-digit', month: 'short', year: '2-digit' })}</p>
                                  <p className="text-[10px] font-black text-indigo-500 uppercase">Jam {entry.startPeriod}-{entry.endPeriod}</p>
                                </td>
                                <td className={`px-2 py-2 ${rowBgClass} border-y`}>
                                  <p className="font-black uppercase leading-none mb-0.5 text-slate-800 dark:text-white">{entry.class || '-'}</p>
                                  <p className="text-[10px] font-bold text-slate-400 line-clamp-1">{entry.subject}</p>
                                </td>
                                <td className={`px-2 py-2 ${rowBgClass} border-y`}>
                                  <p className="font-black leading-tight text-slate-800 dark:text-white line-clamp-1">{entry.topic || (entry.status === 'Jam kosong' ? 'KBM KOSONG' : '-')}</p>
                                  {entry.notes && (
                                    <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 italic line-clamp-1 mt-0.5">
                                      {entry.notes}
                                    </p>
                                  )}
                                  <div className="flex flex-wrap gap-2 mt-1">
                                    {entry.materialLink && (
                                        <button 
                                          onClick={(e) => { e.stopPropagation(); setViewingMaterial({link: entry.materialLink!, topic: entry.topic}); }} 
                                          className="flex items-center gap-1 text-[10px] font-black text-indigo-500 hover:underline"
                                        >
                                            <ExternalLink size={10} /> Lihat Materi
                                        </button>
                                    )}
                                    {entry.documentationUrl && entry.documentationUrl.trim() !== '' && (
                                      <span className="flex items-center gap-1 text-[10px] font-black text-emerald-500">
                                        <CheckCircle2 size={10} /> Dokumentasi
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className={`px-1 py-2 ${rowBgClass} border-y text-center`}>
                                  {tp ? <span className="font-black text-indigo-600 dark:text-indigo-400">{tp.code}</span> : <span className="text-slate-200">-</span>}
                                </td>
                                <td className={`px-1 py-2 ${rowBgClass} border-y text-center`}>
                                  {isTask ? <CheckCircle2 size={14} className="text-green-500 mx-auto" /> : <span className="text-slate-200">-</span>}
                                </td>
                                <td className={`px-1 py-2 ${rowBgClass} border-y text-center`}>
                                  <span className="px-1.5 py-0.5 rounded-md text-[10px] font-black uppercase bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                                    {formatActivityType(entry.activityType)}
                                  </span>
                                </td>
                                <td className={`px-1 py-2 ${rowBgClass} border-y text-center`}>
                                  <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-black uppercase ${entry.status === 'Jam kosong' ? 'bg-rose-100 text-rose-600' : entry.status === 'Belum Selesai' ? 'bg-amber-100 text-amber-600' : 'bg-emerald-100 text-emerald-600'}`}>
                                    {entry.status}
                                  </span>
                                </td>
                                <td className={`px-1 py-2 ${rowBgClass} border-y text-center`}>
                                  <div className="flex justify-center gap-1">
                                    <button onClick={(e) => { e.stopPropagation(); handleCopyAgenda(entry); }} className="p-1 text-slate-400 hover:text-emerald-600 transition-colors" title="Salin"><Copy size={14}/></button>
                                    <button onClick={(e) => { e.stopPropagation(); handleOpenModal(entry); }} className="p-1 text-slate-400 hover:text-indigo-600 transition-colors" title="Edit"><Edit size={14}/></button>
                                    <button onClick={(e) => { e.stopPropagation(); handleDeleteAgenda(entry); }} className="p-1 text-slate-400 hover:text-rose-600 transition-colors" title="Hapus"><Trash2 size={14}/></button>
                                  </div>
                                </td>
                                <td className={`px-1 py-2 ${rowBgClass} rounded-r-xl border-y border-r border-l text-center`}>
                                  {entry.class && !entry.isAttendanceOptional ? (
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); setDirectAttendanceAgenda(entry); }}
                                        className={`flex items-center justify-center gap-1 px-2 py-1 w-full rounded-lg font-black text-[10px] uppercase tracking-tight transition-all shadow-sm active:scale-95 ${
                                            entry.status === 'Jam kosong' ? 'bg-rose-50 text-rose-700 dark:bg-rose-600 dark:text-white' : 
                                            entry.status === 'Belum Selesai' ? 'bg-amber-50 text-amber-700 dark:bg-amber-600 dark:text-white' : 
                                            'bg-cyan-50 text-cyan-700 dark:bg-cyan-600 dark:text-white'
                                        }`}
                                    >
                                        <UserCheck size={12} /> {presentCount}/{totalStudents}
                                    </button>
                                  ) : (
                                    <span className="text-slate-300">-</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                          {sortedRecapAgendas.length === 0 && (
                            <tr>
                              <td colSpan={10} className="py-20 text-center">
                                  <History className="mx-auto mb-4 text-slate-200" size={48} />
                                  <p className="text-slate-400 font-bold italic">Tidak ada agenda yang cocok.</p>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div 
                      className="grid gap-3 md:gap-4 mobile-recap-grid"
                      style={{
                        gridTemplateColumns: `repeat(${11 - cardSize}, minmax(0, 1fr))`
                      }}
                    >
                      <style>{`
                        @media (max-width: 767px) {
                          .mobile-recap-grid {
                            grid-template-columns: repeat(${cardSize <= 3 ? 4 : cardSize <= 5 ? 3 : cardSize <= 8 ? 2 : 1}, minmax(0, 1fr)) !important;
                          }
                          .recap-grid-card {
                            container-type: inline-size;
                            aspect-ratio: 1 / 1;
                            padding: 5cqi !important;
                          }
                          .recap-grid-card .meta-text { font-size: 5cqi !important; margin-bottom: 2cqi !important; line-height: 1.2 !important; }
                          .recap-grid-card .title-text { font-size: 11cqi !important; margin-bottom: 1cqi !important; line-height: 1.2 !important; }
                          .recap-grid-card .topic-text { font-size: 8cqi !important; margin-bottom: 2cqi !important; line-height: 1.3 !important; }
                          .recap-grid-card .badge-text { font-size: 4.5cqi !important; padding: 1.5cqi 2.5cqi !important; border-radius: 2cqi !important; }
                          .recap-grid-card .icon-size { width: 8cqi !important; height: 8cqi !important; }
                          .recap-grid-card .btn-size { font-size: 5.5cqi !important; padding: 2.5cqi !important; border-radius: 3cqi !important; gap: 1.5cqi !important; }
                          .recap-grid-card .gap-size { gap: 1.5cqi !important; margin-bottom: 2cqi !important; }
                          .recap-grid-card .border-t-size { padding-top: 3cqi !important; }
                          .recap-grid-card .no-absen-text { font-size: 5.5cqi !important; height: auto !important; padding: 2cqi !important; }
                        }
                      `}</style>
                      {sortedRecapAgendas.map((entry, idx) => {
                          const isTask = [ActivityType.KBM_DAN_QUIZ, ActivityType.SUMATIF, ActivityType.SAS].includes(entry.activityType || ActivityType.KBM);
                          const tp = objectives.find(o => o.id === entry.tpId);
                          const agendaRecords = attendanceRecords.filter(r => r.agendaId === entry.id);
                          const presentCount = agendaRecords.filter(r => r.status === AttendanceStatus.Hadir).length;
                          const totalStudents = students.filter(s => s.class === entry.class && s.status === 'Aktif').length;

                          const isSolid = false;

                          const cardBgClass = entry.activityType === ActivityType.ADMINISTRASI
                            ? 'bg-purple-50/90 dark:bg-purple-900/60 border-purple-200 dark:border-purple-700/80 text-purple-950 dark:text-purple-100'
                            : (entry.activityType === ActivityType.SUMATIF || entry.activityType === ActivityType.SAS)
                            ? 'bg-sky-50/90 dark:bg-sky-900/60 border-sky-200 dark:border-sky-700/80 text-sky-950 dark:text-sky-100'
                            : entry.activityType === ActivityType.TUGAS_MANDIRI
                            ? 'bg-amber-50/90 dark:bg-amber-900/60 border-amber-200 dark:border-amber-700/80 text-amber-950 dark:text-amber-100'
                            : entry.activityType === ActivityType.KEGIATAN_LOMBA
                            ? 'bg-orange-50/90 dark:bg-orange-900/60 border-orange-200 dark:border-orange-700/80 text-orange-950 dark:text-orange-100'
                            : entry.activityType === ActivityType.KEGIATAN_KESISWAAN
                            ? 'bg-fuchsia-50/90 dark:bg-fuchsia-900/60 border-fuchsia-200 dark:border-fuchsia-700/80 text-fuchsia-950 dark:text-fuchsia-100'
                            : entry.activityType === ActivityType.KEGIATAN_SEKOLAH
                            ? 'bg-slate-100/90 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100'
                            : entry.status === 'Jam kosong' 
                            ? 'bg-rose-50/90 dark:bg-rose-900/60 border-rose-200 dark:border-rose-700/80 text-rose-950 dark:text-rose-100' 
                            : entry.status === 'Belum Selesai'
                            ? 'bg-amber-50/90 dark:bg-amber-900/60 border-amber-200 dark:border-amber-700/80 text-amber-950 dark:text-amber-100'
                            : entry.status === 'Selesai'
                            ? 'bg-emerald-50/90 dark:bg-emerald-900/60 border-emerald-200 dark:border-emerald-700/80 text-emerald-950 dark:text-emerald-100'
                            : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100';

                          // Dynamic styles based on cardSize
                          const styles = {
                            container: [
                              'p-1.5 rounded-lg', // 1 (10 cols)
                              'p-2 rounded-xl', // 2 (9 cols)
                              'p-2 rounded-xl', // 3 (8 cols)
                              'p-2.5 rounded-xl', // 4 (7 cols)
                              'p-2.5 rounded-xl', // 5 (6 cols)
                              'p-3.5 rounded-2xl', // 6 (5 cols)
                              'p-4 rounded-[1.5rem]', // 7 (4 cols) - DEFAULT
                              'p-5 rounded-[2rem]', // 8 (3 cols)
                              'p-7 rounded-[2.5rem]', // 9 (2 cols)
                              'p-10 rounded-[3.5rem]'  // 10 (1 col)
                            ][cardSize - 1],
                            meta: [
                              'text-[4px]', // 1
                              'text-[5px]', // 2
                              'text-[5px]', // 3
                              'text-[6px]', // 4
                              'text-[6px]', // 5
                              'text-[7px]', // 6
                              'text-[8px]', // 7
                              'text-[10px]', // 8
                              'text-xs', // 9
                              'text-sm' // 10
                            ][cardSize - 1],
                            title: [
                              'text-[6px]', // 1
                              'text-[7px]', // 2
                              'text-[8px]', // 3
                              'text-[9px]', // 4
                              'text-[10px]', // 5
                              'text-xs', // 6
                              'text-sm', // 7
                              'text-lg', // 8
                              'text-2xl', // 9
                              'text-4xl' // 10
                            ][cardSize - 1],
                            topic: [
                              'text-[4px]', // 1
                              'text-[5px]', // 2
                              'text-[6px]', // 3
                              'text-[6px]', // 4
                              'text-[7px]', // 5
                              'text-[8px]', // 6
                              'text-[10px]', // 7
                              'text-sm', // 8
                              'text-base', // 9
                              'text-xl' // 10
                            ][cardSize - 1],
                            badge: [
                              'text-[4px] px-0.5 py-0', // 1
                              'text-[4px] px-0.5 py-0', // 2
                              'text-[5px] px-1 py-0', // 3
                              'text-[5px] px-1 py-0', // 4
                              'text-[5px] px-1 py-0', // 5
                              'text-[6px] px-1.5 py-0.5', // 6
                              'text-[7px] px-2 py-0.5', // 7
                              'text-[9px] px-2 py-0.5', // 8
                              'text-xs px-3 py-1', // 9
                              'text-sm px-4 py-1.5' // 10
                            ][cardSize - 1],
                            icon: [4, 5, 6, 7, 8, 10, 12, 14, 18, 24][cardSize - 1],
                            btnText: [
                              'text-[4px]', // 1
                              'text-[5px]', // 2
                              'text-[5px]', // 3
                              'text-[6px]', // 4
                              'text-[6px]', // 5
                              'text-[7px]', // 6
                              'text-[8px]', // 7
                              'text-[10px]', // 8
                              'text-xs', // 9
                              'text-sm' // 10
                            ][cardSize - 1],
                            btnPadding: [
                              'py-0.5 px-0.5', // 1
                              'py-0.5 px-0.5', // 2
                              'py-0.5 px-1', // 3
                              'py-1 px-1', // 4
                              'py-1 px-1', // 5
                              'py-1.5 px-2', // 6
                              'py-2 px-3', // 7
                              'py-2.5 px-4', // 8
                              'py-3 px-6', // 9
                              'py-4 px-8' // 10
                            ][cardSize - 1],
                            spacing: [
                              'mb-0.5', // 1
                              'mb-0.5', // 2
                              'mb-1', // 3
                              'mb-1', // 4
                              'mb-1', // 5
                              'mb-1.5', // 6
                              'mb-2', // 7
                              'mb-3', // 8
                              'mb-5', // 9
                              'mb-8'  // 10
                            ][cardSize - 1],
                            gap: [
                              'gap-0', // 1
                              'gap-0.5', // 2
                              'gap-0.5', // 3
                              'gap-0.5', // 4
                              'gap-0.5', // 5
                              'gap-1', // 6
                              'gap-1', // 7
                              'gap-1', // 8
                              'gap-2', // 9
                              'gap-3'  // 10
                            ][cardSize - 1]
                          };

                          return (
                            <motion.div 
                              key={entry.id} 
                              initial={{ opacity: 0.5, scale: 0.9 }}
                              whileInView={{ opacity: 1, scale: 1 }}
                              viewport={{ once: false, amount: 0.2 }}
                              transition={{ duration: 0.4, ease: "easeOut" }}
                              className={`recap-grid-card ${styles.container} border shadow-sm flex flex-col justify-between transition-all hover:shadow-md cursor-pointer ${cardBgClass} relative overflow-hidden group`}
                              onClick={() => setViewingAgendaDetail(entry)}
                            >
                              {entry.documentationUrl && entry.documentationUrl.trim() !== '' && (
                                <div 
                                  className="absolute inset-0 z-0 opacity-20 dark:opacity-30 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                                  style={{ backgroundImage: `url(${entry.documentationUrl})` }}
                                />
                              )}
                              <div className="relative z-10 flex flex-col h-full justify-between">
                                <div>
                                  <div className={`flex justify-between items-start gap-size ${styles.spacing}`}>
                                    <div className="flex flex-col">
                                      <span className={`meta-text ${styles.meta} font-black uppercase tracking-widest mb-1 text-slate-600 dark:text-slate-300`}>
                                        {safeFormatDate(entry.date, { day: '2-digit', month: 'short' })} • Jam {entry.startPeriod}-{entry.endPeriod}
                                      </span>
                                      <h4 className={`title-text ${styles.title} font-black uppercase tracking-tight text-slate-900 dark:text-white`}>
                                        {entry.class || '-'}
                                      </h4>
                                    </div>
                                    <div className={`flex gap-size ${styles.gap}`}>
                                      <button onClick={(e) => { e.stopPropagation(); handleCopyAgenda(entry); }} className="p-1 rounded-lg transition-colors text-emerald-700 dark:text-emerald-300 hover:bg-emerald-200/60 dark:hover:bg-emerald-800/60" title="Salin"><Copy className="icon-size" size={styles.icon}/></button>
                                      <button onClick={(e) => { e.stopPropagation(); handleOpenModal(entry); }} className="p-1 rounded-lg transition-colors text-indigo-700 dark:text-indigo-300 hover:bg-indigo-200/60 dark:hover:bg-indigo-800/60" title="Edit"><Edit className="icon-size" size={styles.icon}/></button>
                                    </div>
                                  </div>
                                  
                                  <div className={`gap-size ${styles.spacing}`}>
                                    <p className={`topic-text ${styles.topic} font-bold leading-tight line-clamp-3 italic text-slate-900 dark:text-slate-100`}>
                                      "{entry.topic || (entry.status === 'Jam kosong' ? 'KBM KOSONG' : '-')}"
                                    </p>
                                    {entry.notes && (
                                      <p className={`meta-text ${styles.meta} mt-1 italic line-clamp-2 text-slate-600 dark:text-slate-300`}>
                                        {entry.notes}
                                      </p>
                                    )}
                                    <div className="flex flex-wrap gap-1 mt-2">
                                      {entry.materialLink && (
                                        <button 
                                          onClick={(e) => { e.stopPropagation(); setViewingMaterial({link: entry.materialLink!, topic: entry.topic}); }} 
                                          className={`flex items-center gap-1 meta-text ${styles.meta} font-black text-indigo-700 dark:text-indigo-300 hover:underline`}
                                        >
                                          <ExternalLink className="icon-size" size={styles.icon - 2} /> Bahan Ajar
                                        </button>
                                      )}
                                      {entry.documentationUrl && entry.documentationUrl.trim() !== '' && (
                                        <span className={`flex items-center gap-1 meta-text ${styles.meta} font-black text-emerald-700 dark:text-emerald-300`}>
                                          <CheckCircle2 className="icon-size" size={styles.icon - 2} /> Dokumentasi
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  <div className={`flex flex-wrap gap-size ${styles.gap} ${styles.spacing}`}>
                                    <span className={`badge-text ${styles.badge} rounded-lg font-black uppercase tracking-widest ${
                                      false ? '' : (
                                        entry.status === 'Jam kosong' ? 'bg-rose-100 text-rose-900 dark:bg-rose-950/80 dark:text-rose-200 border border-rose-300/80 dark:border-rose-800' : 
                                        entry.status === 'Belum Selesai' ? 'bg-amber-100 text-amber-900 dark:bg-amber-950/80 dark:text-amber-200 border border-amber-300/80 dark:border-amber-800' : 
                                        'bg-emerald-100 text-emerald-900 dark:bg-emerald-950/80 dark:text-emerald-200 border border-emerald-300/80 dark:border-emerald-800'
                                      )
                                    }`}>
                                      {entry.status}
                                    </span>
                                    <span className={`badge-text ${styles.badge} rounded-lg font-black uppercase tracking-widest border bg-slate-200/80 dark:bg-slate-800 text-slate-800 dark:text-slate-200 border-slate-300/80 dark:border-slate-700`}>
                                      {formatActivityType(entry.activityType)} {tp ? tp.code : ''}
                                    </span>
                                  </div>
                                </div>

                                <div className={`border-t-size pt-4 border-t border-slate-300/80 dark:border-slate-700/80`}>
                                  {entry.class && !entry.isAttendanceOptional ? (
                                    <button 
                                      onClick={(e) => { e.stopPropagation(); setDirectAttendanceAgenda(entry); }}
                                      className={`flex items-center justify-center gap-2 btn-size ${styles.btnPadding} w-full rounded-xl font-black ${styles.btnText} uppercase tracking-widest transition-all shadow-sm active:scale-95 ${
                                        false ? '' : (
                                          entry.status === 'Jam kosong' ? 'bg-rose-100 text-rose-900 dark:bg-rose-600 dark:text-white hover:bg-rose-200' : 
                                          entry.status === 'Belum Selesai' ? 'bg-amber-100 text-amber-900 dark:bg-amber-600 dark:text-white hover:bg-amber-200' : 
                                          'bg-cyan-100 text-cyan-900 dark:bg-cyan-600 dark:text-white hover:bg-cyan-200'
                                        )
                                      }`}
                                    >
                                      <UserCheck className="icon-size" size={styles.icon} /> Absensi {presentCount}/{totalStudents}
                                    </button>
                                  ) : (
                                    <div className={`no-absen-text h-10 flex items-center justify-center ${styles.btnText} font-black uppercase tracking-widest text-slate-500 dark:text-slate-400`}>
                                      Tidak Ada Absensi
                                    </div>
                                  )}
                                </div>
                              </div>
                            </motion.div>
                          );
                        })}
                        {sortedRecapAgendas.length === 0 && (
                          <div className="col-span-full py-20 text-center">
                            <History className="mx-auto mb-4 text-slate-200" size={48} />
                            <p className="text-slate-400 font-bold italic">Tidak ada agenda yang cocok.</p>
                          </div>
                        )}
                      </div>
                  )}
                </div>
              </div>
          </div>
        )}

        <div className="mt-8 p-8 bg-slate-50 dark:bg-slate-800/30 rounded-[2.5rem] border border-slate-100 dark:border-slate-800 flex items-start gap-5">
            <div className="p-3 bg-indigo-600 text-white rounded-2xl shadow-lg">
                <Info size={24} />
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 font-medium leading-relaxed">
              <p className="font-black text-slate-800 dark:text-white mb-2 uppercase tracking-widest text-sm">Administrasi Agenda Terintegrasi:</p>
              <ul className="space-y-1 list-disc list-inside">
                <li>Klik tombol <strong>Absensi</strong> di setiap baris agenda untuk mengedit kehadiran siswa untuk pertemuan tersebut secara langsung.</li>
                <li>Data kehadiran yang Anda masukkan akan terikat pada ID Agenda, sehingga laporan menjadi lebih akurat per sesi KBM.</li>
                <li>Gunakan <strong>Filter Kode TP</strong> untuk menelusuri riwayat pengajaran pada kompetensi tertentu.</li>
              </ul>
            </div>
        </div>
      </div>

      {/* Material Viewer Modal */}
      {viewingMaterial && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-[2px] z-[200] flex justify-center items-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-[2rem] shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col relative overflow-hidden border border-slate-100 dark:border-slate-800 animate-fade-in-up">
            <div className="flex justify-between items-center p-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
              <h3 className="font-black text-slate-800 dark:text-white truncate pr-8 tracking-tight">Materi: {viewingMaterial.topic}</h3>
              <div className="flex gap-2">
                <a href={viewingMaterial.link} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-indigo-500 transition-colors p-2 bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 flex items-center gap-1 text-xs font-bold" title="Buka di tab baru">
                  <ExternalLink size={16} /> Buka
                </a>
                <button onClick={() => setViewingMaterial(null)} className="text-slate-400 hover:text-rose-500 transition-colors p-2 bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700" title="Tutup">
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="flex-1 w-full bg-slate-100 dark:bg-slate-950">
              {getEmbeddableLink(viewingMaterial.link) ? (
                <iframe 
                  src={getEmbeddableLink(viewingMaterial.link) || ''} 
                  className="w-full h-full border-0" 
                  allow="autoplay"
                  title="Material Viewer"
                ></iframe>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center">
                  <ExternalLink size={48} className="text-slate-300 mb-4" />
                  <p className="text-slate-500 font-bold mb-4">Materi ini tidak dapat ditampilkan langsung di dalam aplikasi.</p>
                  <a href={viewingMaterial.link} target="_blank" rel="noopener noreferrer" className="px-6 py-3 bg-indigo-600 text-white font-black rounded-xl hover:bg-indigo-700 transition-colors flex items-center gap-2">
                    Buka di Tab Baru <ExternalLink size={16} />
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Copied Agenda Banner */}
      {copiedAgenda && (
        <div className="fixed top-8 left-1/2 -translate-x-1/2 bg-slate-800 dark:bg-slate-700 text-white px-6 py-4 rounded-2xl shadow-2xl z-[100] flex items-center gap-4 animate-fade-in border border-slate-700 dark:border-slate-600">
          <div className="flex flex-col">
            <span className="font-black text-sm text-emerald-400">Agenda Tersalin!</span>
            <span className="text-xs text-slate-300">Klik "CATAT SEKARANG" pada jadwal kosong untuk menempel (paste).</span>
          </div>
          <button onClick={() => setCopiedAgenda(null)} className="p-2 bg-slate-700 dark:bg-slate-600 hover:bg-slate-600 dark:hover:bg-slate-500 rounded-xl transition-colors">
            <X size={16} />
          </button>
        </div>
      )}
    </>
  );
};

export default AgendaView;
